import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Stack,
  List,
  ListItemButton,
  Avatar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GlobalStyles,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  Chip,
  Slider,
  Alert,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardActionArea
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import AddIcon from '@mui/icons-material/Add';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import BrushIcon from '@mui/icons-material/Brush';
import TimelineIcon from '@mui/icons-material/Timeline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import GestureIcon from '@mui/icons-material/Gesture';
import GridOnIcon from '@mui/icons-material/GridOn';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import CollectionsIcon from '@mui/icons-material/Collections';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MovieIcon from '@mui/icons-material/Movie';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CropDinIcon from '@mui/icons-material/CropDin';
import Crop169Icon from '@mui/icons-material/Crop169';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StopIcon from '@mui/icons-material/Stop';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MotionPhotosAutoIcon from '@mui/icons-material/MotionPhotosAuto';
import BackspaceIcon from '@mui/icons-material/Backspace';
import OpacityIcon from '@mui/icons-material/Opacity';
import EditIcon from '@mui/icons-material/Edit';
import * as storyboardService from '../services/storyboardService';
import * as comfyService from '../services/comfyService';
import * as assetService from '../services/assetService';
import { useParams } from 'react-router-dom';
import ThreeDScene from './ThreeDScene'; // Import the ThreeDScene component
import DrawingCanvasComponent from './DrawingCanvas';
import { usePermissions } from '../contexts/PermissionsContext';
import { FEATURE_FLAGS } from '../config';
import qwenEditThreeImageWorkflow from '../workflows/qwen_edit_three_image.json';
// DrawingCanvas will be extracted; for now keep inline and re-exported via DrawingCanvas.js

// Parse aspect ratio strings/numbers into a numeric ratio
// Supports: 1.78, '1.78', '16:9', '2.39:1', '2:35:1' (treated as 2.35:1)
const parseAspectRatio = (val) => {
  if (val == null) return null;
  if (typeof val === 'number' && isFinite(val) && val > 0) return val;
  const s = String(val).trim();
  // If numeric-like string, parse
  if (/^\d*\.?\d+$/.test(s)) {
    const n = parseFloat(s);
    return isFinite(n) && n > 0 ? n : null;
  }
  // Handle colon formats
  if (s.includes(':')) {
    const parts = s.split(':').map(p => p.trim()).filter(Boolean);
    if (parts.length === 2) {
      const a = parseFloat(parts[0]);
      const b = parseFloat(parts[1]);
      if (isFinite(a) && isFinite(b) && b !== 0) return a / b;
    }
    if (parts.length === 3) {
      // Interpret like '2:35:1' => 2.35:1
      const whole = parts[0];
      const frac = parts[1];
      const denom = parts[2];
      const a = parseFloat(`${whole}.${frac}`);
      const b = parseFloat(denom);
      if (isFinite(a) && isFinite(b) && b !== 0) return a / b;
    }
  }
  return null;
};

const loadImageFromSrc = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = (err) => reject(err);
  img.src = src;
});

const cropBase64ToRatio = async (base64, ratioValue) => {
  const ratio = parseAspectRatio(ratioValue) || (Number.isFinite(Number(ratioValue)) ? Number(ratioValue) : null);
  if (!ratio || ratio <= 0) return base64;

  try {
    const img = await loadImageFromSrc(base64);
    const currentRatio = img.width / img.height;
    let cropWidth = img.width;
    let cropHeight = img.height;
    let sx = 0;
    let sy = 0;

    if (currentRatio > ratio) {
      cropWidth = Math.round(img.height * ratio);
      sx = Math.round((img.width - cropWidth) / 2);
    } else if (currentRatio < ratio) {
      cropHeight = Math.round(img.width / ratio);
      sy = Math.round((img.height - cropHeight) / 2);
    } else {
      cropWidth = img.width;
      cropHeight = img.height;
    }

    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to crop image to aspect ratio', error);
    return base64;
  }
};

const blobToDataURL = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = (err) => reject(err);
  reader.readAsDataURL(blob);
});

const dataURLToBlob = (dataUrl) => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0]?.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(arr[1] || '');
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i += 1) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
};

// Default Qwen Image Edit workflow (2511 lightning 4-step, three references)
const DEFAULT_QWEN_EDIT_WORKFLOW = qwenEditThreeImageWorkflow;

const StoryboardFrame = React.memo(({ 
  frame, 
  index, 
  style, 
  moveCard, 
  isDragging: isParentDragging, 
  setIsDragging, 
  frames,
  dragState,
  serverUpdate,
  onClick,
  onDelete,
  onDuplicate,
  isSelected,
  projectId,
  setError,
  setLastSaved
}) => {
  // Get theme and determine if it's dark mode
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const frameRef = useRef(null);
  const imageRef = useRef(null);
  // Use preview when available so drawings appear on the card
  const displayUrl = useMemo(() => (frame.previewUrl || frame.imageUrl || ''), [frame.previewUrl, frame.imageUrl]);
  const imageUrlRef = useRef(displayUrl);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Use the selected prop passed from parent
  const isCurrentFrameDragging = isParentDragging && index === dragState?.current?.initialIndex;
  
  // Update imageUrlRef when frame.imageUrl changes
  useEffect(() => {
    imageUrlRef.current = displayUrl;
  }, [displayUrl]);

  useEffect(() => {
    if (imageRef.current && displayUrl) {
      // Create a new Image object to preload
      const img = new Image();
      img.src = displayUrl;
      
      img.onload = () => {
        if (imageRef.current) {
          imageRef.current.src = displayUrl;
          setIsImageLoaded(true);
        }
      };

      img.onerror = () => {
        console.error('Image failed to load:', displayUrl);
        setIsImageLoaded(false);
      };
    }
  }, [displayUrl]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'frame',
    item: () => {
      // Store the initial state when drag starts
      if (dragState && !dragState.current.isDragging) {
        dragState.current.isDragging = true;
        dragState.current.initialIndex = index;
        dragState.current.currentIndex = index; // Initialize current index too
        dragState.current.originalFrames = [...frames];
        dragState.current.dropHandled = false;
        dragState.current.draggedId = frame._id;
        setIsDragging(true);

        if (process.env.NODE_ENV === 'development') {
          console.log('Starting drag operation from index:', index);
          console.log('Drag state initialized:', {
            initialIndex: dragState.current.initialIndex,
            framesCount: frames.length,
            originalFramesCount: dragState.current.originalFrames.length,
            isDragging: dragState.current.isDragging
          });
        }
      }

      return {
        id: frame._id,
        index,
        originalIndex: dragState.current.initialIndex, // Use the stored initial index
        imageUrl: imageUrlRef.current,
        sceneNumber: frame.sceneNumber,
        shotNumber: frame.shotNumber
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    // Save happens only when card is released (on drag end)
    end: (item, monitor) => {
      // Reset dragging UI state
      setIsDragging(false);

      // Only save if we have valid drag state and position changed
      if (!dragState?.current?.isDragging) return;

      // If BoardDropContainer already handled the drop, skip the redundant save
      if (dragState.current.dropHandled) {
        dragState.current = {
          isDragging: false,
          initialIndex: -1,
          currentIndex: -1,
          originalFrames: [],
          pendingUpdate: false,
          dropHandled: false,
          draggedId: null
        };
        return;
      }

      const initialSourceIndex = dragState.current.initialIndex;
      const finalDestinationIndex = dragState.current.currentIndex;
      // Capture currentFrames BEFORE resetting drag state
      const currentFrames = dragState.current.currentFrames || frames;

      // Reset drag state
      dragState.current = {
        isDragging: false,
        initialIndex: -1,
        currentIndex: -1,
        originalFrames: [],
        pendingUpdate: false,
        dropHandled: false,
        draggedId: null
      };

      // Check if position actually changed
      if (finalDestinationIndex === -1 ||
          initialSourceIndex === -1 ||
          finalDestinationIndex === initialSourceIndex) {
        return;
      }

      // Show saved immediately (optimistic UI)
      if (setLastSaved) {
        const timeStr = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
        setLastSaved(timeStr);
      }
      if (setError) {
        setError({ visible: false, message: '', type: '' });
      }

      // Fire and forget - don't await
      if (projectId) {
        storyboardService.reorderStoryboards(projectId, {
          sourceIndex: initialSourceIndex,
          destinationIndex: finalDestinationIndex,
          frameIds: currentFrames.map(f => f._id)
        }).catch(err => {
          console.error('Background save failed:', err);
        });
      }
    }
  });

  

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'frame',
    hover(item) {
      if (!frameRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      // Move during hover for visual feedback, but don't save yet
      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop(item, monitor) {
      // Just mark the final position - save happens in end() callback
      if (dragState?.current) {
        dragState.current.currentIndex = item.index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const combinedRef = useCallback((node) => {
    frameRef.current = node;
    drag(drop(node));
  }, [drag, drop]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // Determine frame border color (neutralized; no per-scene hue coding)
  const getBorderColor = () => {
    if (isSelected) return theme.palette.primary.main;
    const base = theme.palette.divider;
    return isHovered ? alpha(base, 0.9) : base;
  };

  // Function to handle frame click
  const handleClick = () => {
    // Call the onClick handler if provided
    if (onClick) {
      onClick();
    }
  };

  const { can } = usePermissions();
  const canManage = can('storyboards.manage');
  return (
    <Box
      ref={combinedRef}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      role="button"
      aria-label={`Storyboard frame${frame?.sceneNumber ? ` scene ${frame.sceneNumber}` : ''}${frame?.shotNumber ? ` shot ${frame.shotNumber}` : ''}`}
      sx={{
        position: 'relative',
        borderRadius: 1,
        p: 1.5,
        m: 0.5,
        backgroundColor: isOver
          ? (isDark ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04))
          : (isDark ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper),
        cursor: isCurrentFrameDragging ? 'grabbing' : 'grab',
        opacity: isCurrentFrameDragging ? 0.95 : 1,
        transform: isCurrentFrameDragging
          ? 'scale(1.02)'
          : (isHovered ? 'translateY(-1px)' : 'none'),
        transition: isParentDragging ? 'none' : 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isCurrentFrameDragging
          ? (isDark ? `0 12px 28px ${alpha('#000', 0.4)}` : '0 12px 28px rgba(0,0,0,0.12)')
          : (isSelected 
              ? (isDark ? `0 0 0 2px ${theme.palette.primary.main}` : `0 0 0 2px ${theme.palette.primary.main}`)
              : (isHovered 
                  ? (isDark ? `0 4px 16px ${alpha('#000', 0.3)}` : '0 4px 16px rgba(0,0,0,0.08)')
                  : (isDark ? `0 2px 8px ${alpha('#000', 0.2)}` : '0 1px 4px rgba(0,0,0,0.04)'))),
        width: '100%',
        maxWidth: 260,
        userSelect: 'none',
        border: isSelected 
          ? 'none' 
          : `1px solid ${isDark ? alpha(theme.palette.divider, 0.1) : alpha(theme.palette.divider, 0.6)}`,
        outline: 'none',
        '&:focus-visible': {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          border: 'none'
        }
      }}
    >
      {/* Frame Number Badge - minimal pill style */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          backgroundColor: isDark 
            ? alpha(theme.palette.common.black, 0.6) 
            : alpha(theme.palette.common.white, 0.9),
          color: isDark 
            ? alpha(theme.palette.common.white, 0.9) 
            : theme.palette.text.primary,
          borderRadius: 1,
          minWidth: 24,
          height: 24,
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 600,
          zIndex: 3,
          border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.15) : alpha(theme.palette.divider, 0.2)}`
        }}
      >
        {index + 1}
      </Box>

      {/* Scene/Shot Badge - minimal style */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: isDark 
            ? alpha(theme.palette.common.black, 0.6) 
            : alpha(theme.palette.common.white, 0.9),
          color: isDark 
            ? alpha(theme.palette.common.white, 0.7) 
            : theme.palette.text.secondary,
          borderRadius: 1,
          px: 0.75,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.65rem',
          fontWeight: 500,
          letterSpacing: '0.02em',
          zIndex: 3,
          border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.15) : alpha(theme.palette.divider, 0.2)}`,
          opacity: isHovered ? 1 : 0.85,
          transition: 'opacity 150ms ease'
        }}
      >
        {`S${frame.sceneNumber}.${frame.shotNumber}`}
      </Box>

      {/* Action Buttons - minimal style, bottom-right on hover */}
      {canManage && (
        <Box
          className="action-buttons"
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            display: 'flex',
            gap: 0.5,
            zIndex: 5,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 150ms ease',
          }}
        >
          <Tooltip title="Duplicate frame" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate && onDuplicate(e);
              }}
              sx={{
                backgroundColor: isDark 
                  ? alpha(theme.palette.common.black, 0.6) 
                  : alpha(theme.palette.common.white, 0.9),
                color: theme.palette.text.primary,
                width: 28,
                height: 28,
                border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.15) : alpha(theme.palette.divider, 0.2)}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  border: 'none'
                },
                transition: 'all 150ms ease'
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete frame" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete();
              }}
              sx={{
                backgroundColor: isDark 
                  ? alpha(theme.palette.common.black, 0.6) 
                  : alpha(theme.palette.common.white, 0.9),
                color: theme.palette.error.main,
                width: 28,
                height: 28,
                border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.15) : alpha(theme.palette.divider, 0.2)}`,
                '&:hover': {
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  border: 'none'
                },
                transition: 'all 150ms ease'
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Image Container */}
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          backgroundColor: isDark 
            ? alpha(theme.palette.common.black, 0.2) 
            : alpha(theme.palette.grey[100], 1),
          borderRadius: 1,
          overflow: 'hidden',
          aspectRatio: parseAspectRatio(frame.aspectRatio) || 1.78,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1
        }}
      >
        {(frame.previewUrl || frame.imageUrl) ? (
          <img
            ref={imageRef}
            src={typeof (frame.previewUrl || frame.imageUrl) === 'string' ? (frame.previewUrl || frame.imageUrl) : ''}
            alt={`Frame ${index + 1}`}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              // Render image using saved pan/zoom within masked container
              width: `${(Number(frame.imageScale || 1)) * 100}%`,
              height: 'auto',
              transform: `translate(${Number(frame.imagePosition?.x || 0)}%, ${Number(frame.imagePosition?.y || 0)}%) scale(${(Number(frame.imageScale || 1)) * (isHovered ? 1.03 : 1)})`,
              transformOrigin: '0 0',
              opacity: isImageLoaded ? 1 : 0,
              transition: 'transform 0.3s ease, opacity 0.3s ease'
            }}
            onError={() => setIsImageLoaded(false)}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              padding: 2
            }}
          >
            <FileUploadIcon sx={{ 
              fontSize: 36, 
              color: isDark 
                ? alpha(theme.palette.primary.main, 0.5) 
                : alpha(theme.palette.primary.main, 0.4),
              mb: 1
            }} />
            <Typography variant="caption" align="center" sx={{ 
              color: isDark 
                ? alpha(theme.palette.common.white, 0.6) 
                : alpha(theme.palette.common.black, 0.6),
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              Click to add image
            </Typography>
          </Box>
        )}
      </Box>



      {/* Frame Info - minimal */}
      <Box sx={{ 
        mt: 1.25, 
        display: 'flex', 
        flexDirection: 'column',
        gap: 0.25
      }}>
        {/* Shot Type */}
        <Typography
          variant="caption"
          sx={{
            color: isDark 
              ? alpha(theme.palette.common.white, 0.5) 
              : theme.palette.text.disabled,
            fontWeight: 500,
            fontSize: '0.6rem',
            lineHeight: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          {frame.shotType 
            ? frame.shotType
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ') 
            : 'Medium Shot'}
        </Typography>
        
        {/* Description */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: isDark 
              ? alpha(theme.palette.common.white, 0.85) 
              : theme.palette.text.primary,
            fontWeight: 500,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {frame.description || `Scene ${frame.sceneNumber}, Shot ${frame.shotNumber}`}
        </Typography>
      </Box>
    </Box>
  );
});

StoryboardFrame.displayName = 'StoryboardFrame';

// Container that attaches a board-level drop target; must be used INSIDE DndProvider
const BoardDropContainer = ({ frames, dragState, lastMoveOp, serverUpdate, sx, children }) => {
  const [, boardDrop] = useDrop({
    accept: 'frame',
    drop() {
      if (!dragState?.current) return;
      if (dragState.current.dropHandled) return;
      const initialSourceIndex = dragState.current.initialIndex;
      let finalDestinationIndex =
        dragState.current.currentIndex !== -1
          ? dragState.current.currentIndex
          : (lastMoveOp.current.hoverIndex !== -1
              ? lastMoveOp.current.hoverIndex
              : initialSourceIndex);

      if (
        initialSourceIndex !== -1 &&
        finalDestinationIndex !== -1 &&
        finalDestinationIndex !== initialSourceIndex
      ) {
        let currentFrames = dragState.current.currentFrames;
        if (!currentFrames) {
          // Build expected ordering by applying the move
          const temp = [...frames];
          const [moved] = temp.splice(initialSourceIndex, 1);
          temp.splice(finalDestinationIndex, 0, moved);
          currentFrames = temp;
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('BOARD drop finalize', { initialSourceIndex, finalDestinationIndex });
        }
        dragState.current.pendingUpdate = true;
        dragState.current.dropHandled = true;
        serverUpdate(
          currentFrames,
          initialSourceIndex,
          finalDestinationIndex,
          dragState.current.originalFrames || currentFrames
        );
      }
    },
  });

  return (
    <Box ref={boardDrop} sx={sx}>
      {children}
    </Box>
  );
};

export const DrawingCanvas = ({ frame, onSave, aspectRatio, onClose }) => {
  return <DrawingCanvasComponent frame={frame} onSave={onSave} aspectRatio={aspectRatio} onClose={onClose} />;
};

// Reusable MUI confirm dialog — replaces window.confirm for a consistent UI
const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onClose, confirmColor = 'primary' }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title || 'Confirm'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{cancelLabel}</Button>
        <Button onClick={onConfirm} color={confirmColor} variant="contained">{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

// Shared duplicate-shot-number retry helper.
// `saveFn(frame)` performs the actual save; `askFn(message)` returns a Promise<boolean> for user confirmation.
// Returns { success, error } — caller handles UI state accordingly.
const retrySaveWithShotNumber = async (updatedFrame, sceneNumber, shotNumber, apiError, saveFn, askFn, existingFrames) => {
  if (apiError.suggestedShotNumber) {
    const userConfirmed = await askFn(
      `A storyboard with scene ${sceneNumber}, shot ${shotNumber} already exists. ` +
      `Would you like to use shot number ${apiError.suggestedShotNumber} instead?`
    );
    if (!userConfirmed) {
      return { success: false, error: `A storyboard with scene ${sceneNumber}, shot ${shotNumber} already exists. Please choose a different shot number.` };
    }
    try {
      const retryFrame = { ...updatedFrame, shotNumber: apiError.suggestedShotNumber };
      const response = await saveFn(retryFrame);
      return { success: true, response, shotNumber: apiError.suggestedShotNumber };
    } catch (retryError) {
      console.error('Error on retry with suggested shot number:', retryError);
      return { success: false, error: `Failed to save frame with suggested shot number: ${retryError.message}` };
    }
  }
  // Fallback: 409 without suggested shot number — compute next available
  const status = apiError?.status || apiError?.response?.status;
  const msg = (apiError?.message || '').toLowerCase();
  const looksLikeDuplicate = status === 409 || msg.includes('already exists');
  if (looksLikeDuplicate) {
    const sameScene = (existingFrames || []).filter(f => Number(f.sceneNumber) === Number(sceneNumber));
    const maxShot = sameScene.length > 0 ? Math.max(...sameScene.map(f => Number(f.shotNumber) || 0)) : (Number(shotNumber) || 0);
    const proposed = (isFinite(maxShot) ? maxShot : 0) + 1;
    const userConfirmed = await askFn(
      `A storyboard with scene ${sceneNumber}, shot ${shotNumber} already exists. Would you like to use shot number ${proposed} instead?`
    );
    if (!userConfirmed) {
      return { success: false, error: `A storyboard with scene ${sceneNumber}, shot ${shotNumber} already exists. Please choose a different shot number.` };
    }
    try {
      const retryFrame = { ...updatedFrame, shotNumber: proposed };
      const response = await saveFn(retryFrame);
      return { success: true, response, shotNumber: proposed };
    } catch (retryError) {
      console.error('Error on retry with computed next shot number:', retryError);
      return { success: false, error: `Failed to save frame with shot ${proposed}: ${retryError.message}` };
    }
  }
  // Non-duplicate error
  return { success: false, error: `Failed to save frame: ${apiError.message || 'An unknown error occurred'}` };
};

// Default ComfyUI workflow for z-image (turbo) generation
export const DEFAULT_Z_IMAGE_WORKFLOW = {
  "9": { "inputs": { "filename_prefix": "z-image", "images": ["57:8", 0] }, "class_type": "SaveImage" },
  "58": { "inputs": { "value": "" }, "class_type": "PrimitiveStringMultiline" },
  "61": { "inputs": { "string_a": "Pixel art style,", "string_b": ["58", 0], "delimiter": "" }, "class_type": "StringConcatenate" },
  "57:30": { "inputs": { "clip_name": "qwen_3_4b.safetensors", "type": "lumina2", "device": "default" }, "class_type": "CLIPLoader" },
  "57:29": { "inputs": { "vae_name": "ae.safetensors" }, "class_type": "VAELoader" },
  "57:33": { "inputs": { "conditioning": ["57:27", 0] }, "class_type": "ConditioningZeroOut" },
  "57:8": { "inputs": { "samples": ["57:3", 0], "vae": ["57:29", 0] }, "class_type": "VAEDecode" },
  "57:28": { "inputs": { "unet_name": "z_image_turbo_bf16.safetensors", "weight_dtype": "default" }, "class_type": "UNETLoader" },
  "57:27": { "inputs": { "text": ["58", 0], "clip": ["57:30", 0] }, "class_type": "CLIPTextEncode" },
  "57:13": { "inputs": { "width": 1024, "height": 1024, "batch_size": 1 }, "class_type": "EmptySD3LatentImage" },
  "57:3": {
    "inputs": {
      "seed": 0,
      "steps": 4,
      "cfg": 1,
      "sampler_name": "res_multistep",
      "scheduler": "simple",
      "denoise": 1,
      "model": ["57:11", 0],
      "positive": ["57:27", 0],
      "negative": ["57:33", 0],
      "latent_image": ["57:13", 0]
    },
    "class_type": "KSampler"
  },
  "57:11": { "inputs": { "shift": 3, "model": ["57:28", 0] }, "class_type": "ModelSamplingAuraFlow" }
};

// Shared image processing helpers — used by both EditFrameDialog and Storyboard dropzones
const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => {
    console.error('FileReader error:', error);
    reject(new Error('Failed to read file'));
  };
  reader.readAsDataURL(file);
});

const loadImageElement = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = (error) => {
    console.error('Image load error:', error);
    reject(new Error('Failed to load image'));
  };
  image.src = src;
});

// Resize an image to fit within maxWidth/maxHeight, returning a JPEG data URL + metadata
const processImageToDataURL = (img, maxWidth = 1920, maxHeight = 1080, quality = 0.85) => {
  let targetWidth = img.width;
  let targetHeight = img.height;
  if (targetWidth > maxWidth || targetHeight > maxHeight) {
    const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
    targetWidth *= ratio;
    targetHeight *= ratio;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  const processedImageUrl = canvas.toDataURL('image/jpeg', quality);
  const matches = processedImageUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
  if (!matches || matches.length !== 3) {
    throw new Error('Invalid image data format');
  }
  return {
    dataUrl: processedImageUrl,
    width: targetWidth,
    height: targetHeight,
    mimeType: matches[1],
    compressedSize: Math.ceil(matches[2].length * 0.75)
  };
};


// Shared form fields for the Qwen AI dialog (used by both z-image and composite modes)
const QwenFormFields = ({
  prompt, onPromptChange,
  seed, onSeedChange,
  autoRandomSeed, onAutoRandomSeedChange,
  variationStrength, onVariationStrengthChange,
  shotSizePrompt, onShotSizeChange,
  lensPrompt, onLensChange,
  anglePrompt, onAngleChange,
  aiAspectRatio, onAspectRatioChange,
  aiTimeOfDay, onTimeOfDayChange,
  aiAtmosphere, onAtmosphereChange,
  aiLightingStyle, onLightingChange,
  aiCompositionStyle, onCompositionChange,
  aiCompositionFocus, onCompositionFocusChange,
  shotSizeOptions, lensOptions, angleOptions, aspectRatioOptions,
  timeOfDayOptions, atmosphereOptions, lightingOptions, compositionOptions,
  ruleOfThirdsFocusLabelMap, ruleOfThirdsGridCells,
  promptLabel, promptPlaceholder
}) => (
  <>
    <TextField
      size="small"
      label={promptLabel || 'Prompt'}
      multiline
      minRows={3}
      value={prompt}
      onChange={(e) => onPromptChange(e.target.value)}
      placeholder={promptPlaceholder || 'Describe what to generate'}
    />
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        label="Seed"
        value={seed}
        onChange={(e) => onSeedChange(e.target.value)}
        disabled={autoRandomSeed}
        helperText={autoRandomSeed ? 'Auto-random' : 'Set for reproducible results'}
      />
      <FormControlLabel
        control={<Switch size="small" checked={autoRandomSeed} onChange={(e) => onAutoRandomSeedChange(e.target.checked)} />}
        label="Auto seed"
      />
    </Stack>
    <Box sx={{ px: 1, mt: -1 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Variation</Typography>
      <Slider
        size="small"
        min={0}
        max={1}
        step={0.05}
        value={variationStrength}
        onChange={(_, v) => onVariationStrengthChange(Array.isArray(v) ? v[0] : v)}
        valueLabelDisplay="auto"
      />
    </Box>
    <Grid container spacing={1}>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Shot size</InputLabel>
          <Select value={shotSizePrompt} label="Shot size" onChange={(e) => onShotSizeChange(e.target.value)}>
            {shotSizeOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Lens</InputLabel>
          <Select value={lensPrompt} label="Lens" onChange={(e) => onLensChange(e.target.value)}>
            {lensOptions.map(val => <MenuItem key={val || 'none'} value={val}>{val || 'None'}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Angle</InputLabel>
          <Select value={anglePrompt} label="Angle" onChange={(e) => onAngleChange(e.target.value)}>
            {angleOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Aspect ratio</InputLabel>
          <Select value={aiAspectRatio} label="Aspect ratio" onChange={(e) => onAspectRatioChange(e.target.value)}>
            {aspectRatioOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Time of day</InputLabel>
          <Select value={aiTimeOfDay} label="Time of day" onChange={(e) => onTimeOfDayChange(e.target.value)}>
            {timeOfDayOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Atmosphere</InputLabel>
          <Select value={aiAtmosphere} label="Atmosphere" onChange={(e) => onAtmosphereChange(e.target.value)}>
            {atmosphereOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Lighting</InputLabel>
          <Select value={aiLightingStyle} label="Lighting" onChange={(e) => onLightingChange(e.target.value)}>
            {lightingOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Composition</InputLabel>
          <Select value={aiCompositionStyle} label="Composition" onChange={(e) => onCompositionChange(e.target.value)}>
            {compositionOptions.map(opt => <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      {aiCompositionStyle?.toLowerCase().includes('rule of thirds') && (
        <Grid item xs={12} sm={8} md={6}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>Rule of thirds subject placement</Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 1,
              overflow: 'hidden',
              background: 'linear-gradient(90deg, transparent 32.5%, rgba(255,0,0,0.55) 33.333%, rgba(255,0,0,0.55) 33.833%, transparent 34.333%, transparent 65.667%, rgba(255,0,0,0.55) 66.667%, rgba(255,0,0,0.55) 67.167%, transparent 67.667%), linear-gradient(180deg, transparent 32.5%, rgba(255,0,0,0.55) 33.333%, rgba(255,0,0,0.55) 33.833%, transparent 34.333%, transparent 65.667%, rgba(255,0,0,0.55) 66.667%, rgba(255,0,0,0.55) 67.167%, transparent 67.667%), #b0b0b0'
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)' }}>
              {ruleOfThirdsGridCells.map((cell) => {
                const selected = aiCompositionFocus === cell.value;
                return (
                  <Box
                    key={cell.value}
                    onClick={() => onCompositionFocusChange(cell.value)}
                    sx={{
                      border: selected ? '2px solid' : '1px solid transparent',
                      borderColor: selected ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: selected ? 'primary.main' : 'text.primary',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      px: 0.5,
                      userSelect: 'none',
                      gridRowStart: cell.row + 1,
                      gridColumnStart: cell.col + 1,
                    }}
                    title={cell.label}
                  >
                    {cell.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <FormHelperText>Select an intersection/line for the subject</FormHelperText>
        </Grid>
      )}
    </Grid>
  </>
);


const EditFrameDialog = ({ open, frame, onClose, onSave, projectId, pendingAsset, onConsumePendingAsset, autoOpenQwen, onConsumeAutoOpenQwen, variantOnly, onSetVariantOnly }) => {
  // Use a default aspect ratio constant instead of trying to access this.state
  const defaultAspectRatio = 16/9; // Common default aspect ratio for storyboards
  
  // Shot types for dropdown selection
  const shotTypes = [
    { value: 'extreme-wide-shot', label: 'Extreme Wide Shot (EWS)' },
    { value: 'wide-shot', label: 'Wide Shot (WS)' },
    { value: 'cowboy-shot', label: 'Cowboy framing (mid-thigh crop)' },
    { value: 'full-shot', label: 'Full Shot (FS)' },
    { value: 'medium-shot', label: 'Medium Shot (MS)' },
    { value: 'medium-close-up', label: 'Medium Close-Up (MCU)' },
    { value: 'close-up', label: 'Close-Up (CU)' },
    { value: 'extreme-close-up', label: 'Extreme Close-Up (ECU)' },
    { value: 'over-the-shoulder', label: 'Over The Shoulder (OTS)' },
    { value: 'point-of-view', label: 'Point of View (POV)' },
    { value: 'aerial', label: 'Aerial Shot' },
    { value: 'establishing', label: 'Establishing Shot' },
    { value: 'other', label: 'Other' }
  ];
  
  const [editedFrame, setEditedFrame] = useState(frame || {
    sceneNumber: '1',
    shotNumber: '1',
    description: '',
    shotType: 'medium-shot', // Default shot type
    drawings: [],
    aspectRatio: frame?.aspectRatio || defaultAspectRatio,
    imageUrl: null,
    previewUrl: null,
    layers: [],
    currentLayerId: null
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const resetImageTransform = useCallback(() => {
    setScale(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [showCGScene, setShowCGScene] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [assets, setAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetError, setAssetError] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genSeed, setGenSeed] = useState('0');
  const [genType, setGenType] = useState('character');
  const [genName, setGenName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [tabValue, setTabValue] = useState('preview');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const [showQwenDialog, setShowQwenDialog] = useState(false);
  const [qwenPrompt, setQwenPrompt] = useState('');
  const [qwenSeed, setQwenSeed] = useState('0');
  const [qwenReferenceId, setQwenReferenceId] = useState('');
  const [qwenMode, setQwenMode] = useState('composite'); // composite | variation
  const [isQwenGenerating, setIsQwenGenerating] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [autoRandomSeed, setAutoRandomSeed] = useState(true);
  const [variationStrength, setVariationStrength] = useState(0.35);
  const [shotSizePrompt, setShotSizePrompt] = useState('');
  const [lensPrompt, setLensPrompt] = useState('');
  const [anglePrompt, setAnglePrompt] = useState('');
  const [aiAspectRatio, setAiAspectRatio] = useState('');
  const [aiLightingStyle, setAiLightingStyle] = useState('');
  const [aiAtmosphere, setAiAtmosphere] = useState('');
  const [aiCompositionStyle, setAiCompositionStyle] = useState('');
  const [aiCompositionFocus, setAiCompositionFocus] = useState('');
  const [aiTimeOfDay, setAiTimeOfDay] = useState('');

  const resetAiFields = useCallback(() => {
    setShotSizePrompt('');
    setLensPrompt('');
    setAnglePrompt('');
    setAiAspectRatio('');
    setAiLightingStyle('');
    setAiAtmosphere('');
    setAiCompositionStyle('');
    setAiCompositionFocus('');
    setAiTimeOfDay('');
    setQwenPrompt('');
    setQwenSeed('0');
    setQwenReferenceId('');
    setQwenMode('composite');
    setAutoRandomSeed(true);
    setVariationStrength(0.35);
    setGenPrompt('');
    setGenSeed('0');
    setGenName('');
  }, []);

  const computeSeed = useCallback((seedStr, applyVariation = true) => {
    const base = autoRandomSeed ? Math.floor(Math.random() * 10_000_000) : (Number.isFinite(parseInt(seedStr, 10)) ? parseInt(seedStr, 10) : 0);
    if (!applyVariation) return base;
    // Variation adds a random offset scaled by variationStrength
    const jitter = Math.floor(variationStrength * 1_000_000 * Math.random());
    return base + jitter;
  }, [autoRandomSeed, variationStrength]);

  const handleCloseQwenDialog = useCallback(() => {
    setShowQwenDialog(false);
    setSelectedAssets([]);
    resetAiFields();
  }, [resetAiFields]);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const displayPreviewUrl = useMemo(() => {
    const val = editedFrame.previewUrl || editedFrame.imageUrl || frame?.previewUrl || frame?.imageUrl || '';
    if (typeof val === 'string') return val.trim();
    return '';
  }, [editedFrame.previewUrl, editedFrame.imageUrl, frame]);

  const shotLabelMap = useMemo(() => shotTypes.reduce((acc, cur) => {
    acc[cur.value] = cur.label.replace(/ \(.*\)/, '');
    return acc;
  }, {}), [shotTypes]);

  const shotSizeOptions = useMemo(() => ([{ value: '', label: 'None' }, ...shotTypes.map(s => ({ value: s.value, label: shotLabelMap[s.value] || s.label }))]), [shotTypes, shotLabelMap]);

  const lensOptions = useMemo(() => ([
    '', '10mm','14mm','18mm','20mm','24mm','28mm','35mm','40mm','50mm','65mm','85mm','100mm','135mm','200mm'
  ]), []);

  const angleOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'High angle', label: 'High' },
    { value: 'Low angle', label: 'Low' },
    { value: "Bird's eye view", label: "Bird's eye" },
    { value: 'Eye level', label: 'Eye level' },
    { value: 'Dutch angle', label: 'Dutch' },
  ]), []);

  const aspectRatioOptions = useMemo(() => ([
    { value: '', label: 'Default' },
    { value: '1.33', label: '4:3 (1.33:1)' },
    { value: '1.37', label: 'Academy (1.37:1)' },
    { value: '1.66', label: '1.66:1' },
    { value: '1.78', label: '16:9 (1.78:1)' },
    { value: '1.85', label: '1.85:1' },
    { value: '2.39', label: '2.39:1 Scope' },
    { value: '2.76', label: '2.76:1 Ultra Panavision' },
    { value: '1.43', label: 'IMAX 1.43:1' },
    { value: '1.0', label: '1:1 Square' },
  ]), []);

  const aspectRatioLabelMap = useMemo(() => aspectRatioOptions.reduce((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), [aspectRatioOptions]);

  const lightingOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Golden hour warm directional light', label: 'Golden hour' },
    { value: 'Rembrandt lighting with soft key and triangle fill', label: 'Rembrandt' },
    { value: 'Low key, chiaroscuro contrast', label: 'Low key' },
    { value: 'High key soft diffused light', label: 'High key' },
    { value: 'Backlit rim light separating subject', label: 'Backlight' },
    { value: 'Neon practicals with colored gels', label: 'Neon practicals' },
  ]), []);

  const atmosphereOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Soft mist', label: 'Soft mist' },
    { value: 'Thick fog', label: 'Thick fog' },
    { value: 'Haze', label: 'Haze' },
    { value: 'Volumetric light beams', label: 'Volumetric light beams' },
    { value: 'Dust in air', label: 'Dust in air' },
    { value: 'Smoke', label: 'Smoke' },
  ]), []);

  const timeOfDayOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Dawn', label: 'Dawn' },
    { value: 'Sunrise', label: 'Sunrise' },
    { value: 'Morning', label: 'Morning' },
    { value: 'Noon', label: 'Noon' },
    { value: 'Afternoon', label: 'Afternoon' },
    { value: 'Golden hour', label: 'Golden hour' },
    { value: 'Sunset', label: 'Sunset' },
    { value: 'Blue hour', label: 'Blue hour' },
    { value: 'Night', label: 'Night' },
    { value: 'Midnight', label: 'Midnight' },
  ]), []);

  const compositionOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Rule of thirds framing with negative space', label: 'Rule of thirds' },
    { value: 'Leading lines driving eye to subject', label: 'Leading lines' },
    { value: 'Deep focus with foreground/midground/background layers', label: 'Layered depth' },
    { value: 'Shallow depth of field isolating subject', label: 'Shallow DOF' },
    { value: 'Symmetrical centered composition', label: 'Symmetry' },
    { value: 'Off-center with strong silhouette', label: 'Silhouette' },
  ]), []);

  const ruleOfThirdsFocusOptions = useMemo(() => ([
    { value: '', label: 'No focus' },
    { value: 'center punch', label: 'Center punch' },
    { value: 'top-left intersection', label: 'Top left' },
    { value: 'top-right intersection', label: 'Top right' },
    { value: 'bottom-left intersection', label: 'Bottom left' },
    { value: 'bottom-right intersection', label: 'Bottom right' },
    { value: 'upper horizontal line', label: 'Upper horizontal' },
    { value: 'lower horizontal line', label: 'Lower horizontal' },
    { value: 'left vertical line', label: 'Left vertical' },
    { value: 'right vertical line', label: 'Right vertical' },
  ]), []);

  const ruleOfThirdsFocusLabelMap = useMemo(() => ruleOfThirdsFocusOptions.reduce((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), [ruleOfThirdsFocusOptions]);

  const ruleOfThirdsGridCells = useMemo(() => ([
    { value: 'center punch', row: 1, col: 1, label: 'Center' },
    { value: 'top-left intersection', row: 0, col: 0, label: 'Top left' },
    { value: 'top-right intersection', row: 0, col: 2, label: 'Top right' },
    { value: 'bottom-left intersection', row: 2, col: 0, label: 'Bottom left' },
    { value: 'bottom-right intersection', row: 2, col: 2, label: 'Bottom right' },
    { value: 'upper horizontal line', row: 0, col: 1, label: 'Upper line' },
    { value: 'lower horizontal line', row: 2, col: 1, label: 'Lower line' },
    { value: 'left vertical line', row: 1, col: 0, label: 'Left vertical' },
    { value: 'right vertical line', row: 1, col: 2, label: 'Right vertical' },
  ]), []);

  const assetCategories = useMemo(() => ([
    { key: 'character', label: 'Characters' },
    { key: 'location', label: 'Locations' },
    { key: 'vehicle', label: 'Vehicles' },
    { key: 'prop', label: 'Props' },
    { key: 'other', label: 'Other' }
  ]), []);

  const groupedAssets = useMemo(() => {
    const groups = {};
    (assets || []).forEach((asset) => {
      const key = asset.type || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(asset);
    });

    const ordered = assetCategories
      .map(cat => ({ ...cat, items: groups[cat.key] || [] }))
      .filter(cat => cat.items.length > 0);

    // Append any unknown categories
    Object.entries(groups).forEach(([key, items]) => {
      if (!assetCategories.find(c => c.key === key)) {
        ordered.push({ key, label: key.charAt(0).toUpperCase() + key.slice(1), items });
      }
    });

    return ordered;
  }, [assets, assetCategories]);

  const isNewFrame = !frame?._id;

  const buildPromptWithMods = useCallback((basePrompt) => {
    const parts = [];
    if (basePrompt && basePrompt.trim()) parts.push(basePrompt.trim());
    const shotLabel = shotLabelMap[shotSizePrompt];
    if (shotLabel) parts.push(`Shot: ${shotLabel}`);
    if (lensPrompt) parts.push(`Lens: ${lensPrompt}`);
    if (anglePrompt) {
      parts.push(`Angle: ${anglePrompt}`);
      if (anglePrompt.toLowerCase().includes('dutch')) {
        parts.push('Camera is noticeably canted off-axis (dutch angle), horizon intentionally tilted.');
      }
    }
    const arLabel = aspectRatioLabelMap[aiAspectRatio];
    if (arLabel) parts.push(`Aspect ratio: ${arLabel}`);
    if (aiTimeOfDay) {
      parts.push(`Time of day: ${aiTimeOfDay}`);
      if (aiTimeOfDay.toLowerCase() === 'night') {
        parts.push('Scene is fully night-time: dark sky, no sunset or dusk colors, no twilight glow.');
        parts.push('Avoid dusk, sunset, or twilight coloration.');
      }
    }
    if (aiAtmosphere) {
      parts.push(`Atmosphere: ${aiAtmosphere}`);
      const atm = aiAtmosphere.toLowerCase();
      if (atm.includes('haze') || atm.includes('fog') || atm.includes('mist')) {
        parts.push('Add medium-heavy atmospheric haze uniformly (foreground to background, floor to ceiling); soften contrast; keep subject readable; no smoke or steam plumes.');
      }
      parts.push('Cinematic atmosphere: uniform ambient haze blankets the entire volume, soft low-contrast look; light is broad and diffused; no visible light shafts or cones—only gentle overall glow; dust motes visible; subject remains readable; avoid localized haze plumes or spotlight cones.');
      parts.push('Screens/monitors emit soft, diffused glow only—no volumetric beams from displays; keep environmental lighting soft and shaft-free.');
    }
    if (aiLightingStyle) {
      parts.push(`Lighting: ${aiLightingStyle}`);
    } else if (aiAtmosphere) {
      parts.push('Cinematic lighting: strong directional key with volumetric shafts, soft fill, practicals dim, high contrast but subject readable.');
    }
    if (aiCompositionStyle) parts.push(`Composition: ${aiCompositionStyle}`);
    if (aiCompositionStyle?.toLowerCase().includes('rule of thirds')) {
      const focus = aiCompositionFocus || 'center punch';
      const focusLabel = ruleOfThirdsFocusLabelMap[focus] || focus;
      parts.push(`Place the main subject toward the ${focusLabel.toLowerCase()} third of the frame, without any grid lines or overlays.`);
    }
    return parts.join('\n');
  }, [shotSizePrompt, lensPrompt, anglePrompt, aiAspectRatio, aiLightingStyle, aiCompositionStyle, aiCompositionFocus, shotLabelMap, aspectRatioLabelMap, ruleOfThirdsFocusLabelMap]);

  const buildQwenPrompt = useCallback(() => buildPromptWithMods(qwenPrompt), [qwenPrompt, buildPromptWithMods]);

  const handleCompositionFocusChange = useCallback((value) => {
    setAiCompositionFocus(value);
  }, []);

  const handleCompositionChange = useCallback((value) => {
    setAiCompositionStyle(value);
    if (!value?.toLowerCase().includes('rule of thirds')) {
      setAiCompositionFocus('');
    } else if (!aiCompositionFocus) {
      setAiCompositionFocus('center punch');
    }
  }, []);

  const handleShotSizeChange = useCallback((value) => {
    setShotSizePrompt(value);
    const nextShotType = value || 'medium-shot';
    setEditedFrame((prev) => ({ ...prev, shotType: nextShotType }));
  }, []);

  // Sync incoming frame data when dialog opens (e.g., duplicate flow) to ensure preview is available
  useEffect(() => {
    if (open && frame) {
      setEditedFrame((prev) => ({ ...prev, ...frame }));
    }
  }, [open, frame]);

  // Apply pending asset from library when dialog opens
  useEffect(() => {
    if (pendingAsset && open) {
      setEditedFrame((prev) => ({
        ...prev,
        imageUrl: pendingAsset.imageUrl || prev.imageUrl,
        previewUrl: pendingAsset.imageUrl || prev.previewUrl
      }));
      if (onConsumePendingAsset) {
        onConsumePendingAsset();
      }
    }
  }, [pendingAsset, open, onConsumePendingAsset]);

  // Ensure closing the dialog or top-right X while in canvas exits canvas first
  const handleDialogClose = useCallback(() => {
    // Exit drawing mode if active, else close dialog
    if (isDrawing) {
      setIsDrawing(false);
      return;
    }
    setSelectedAssets([]);
    onSetVariantOnly && onSetVariantOnly(false);
    onClose();
  }, [isDrawing, onClose]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxSize: 10485760, // 10MB
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setSaveError('Some files were rejected. Please ensure files are images under 10MB.');
        return;
      }

      for (const file of acceptedFiles) {
        try {
          setIsSaving(true);
          setSaveError(null);

          const imageData = await readFileAsDataURL(file);
          const img = await loadImageElement(imageData);
          const processed = processImageToDataURL(img);

          // Update frame with the processed image
          setEditedFrame(prev => ({
            ...prev,
            imageUrl: processed.dataUrl,
            drawings: [],
            imageMetadata: {
              originalSize: file.size,
              processedSize: processed.compressedSize,
              width: processed.width,
              height: processed.height,
              type: processed.mimeType,
              quality: 0.85
            }
          }));

          setSaveError(null);
        } catch (err) {
          console.error('Error processing image:', err);
          setSaveError(err.message || 'Failed to process image');
        } finally {
          setIsSaving(false);
        }
      }
    }
  });

  // Calculate container dimensions based on aspect ratio — responsive to window resize
  const aspectRatio = parseFloat(editedFrame.aspectRatio || '1.78');
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const containerWidth = Math.min(viewportWidth - 48, 1200);
  const containerHeight = Math.round(containerWidth / aspectRatio);

  useEffect(() => {
    if (autoOpenQwen) {
      setQwenMode('composite');
      setShowQwenDialog(true);
      if (typeof onConsumeAutoOpenQwen === 'function') {
        onConsumeAutoOpenQwen();
      }
    }
  }, [autoOpenQwen, onConsumeAutoOpenQwen]);

  // Enforce composite-only when variantOnly is true
  useEffect(() => {
    if (variantOnly) {
      setQwenMode('composite');
    }
  }, [variantOnly]);

  const loadAssets = useCallback(async () => {
    if (!projectId) return;
    setIsLoadingAssets(true);
    setAssetError(null);
    try {
      const data = await assetService.listAssets(projectId);
      setAssets(data || []);
    } catch (err) {
      console.error('Error loading assets', err);
      setAssetError('Unable to load asset library');
    } finally {
      setIsLoadingAssets(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    if (open) {
      loadAssets();
    }
  }, [open, loadAssets]);

  useEffect(() => {
    if (frame && frame._id) {
      // Editing an existing frame: keep its image transform
      setScale(Number(frame.imageScale || 1));
      setEditedFrame({
        ...frame,
        shotType: frame.shotType || 'medium-shot',
        aspectRatio: frame.aspectRatio || defaultAspectRatio
      });
    } else {
      setScale(1);
      setImagePosition({ x: 0, y: 0 });
      setPreviewLoaded(false);
      setEditedFrame({
        sceneNumber: '1',
        shotNumber: '1',
        description: '',
        shotType: 'medium-shot',
        drawings: [],
        aspectRatio: defaultAspectRatio,
        imageUrl: null,
        previewUrl: null,
        cameraAngle: 'Other',
        movement: 'Static',
        duration: 0,
        layers: [],
        currentLayerId: null,
        notes: '',
        imagePosition: { x: 0, y: 0 },
        imageScale: 1
      });
    }
  }, [frame, defaultAspectRatio]);

  // Track preview image loading to avoid showing empty frame
  useEffect(() => {
    setPreviewLoaded(false);
    if (!displayPreviewUrl) return;
    const img = new Image();
    img.onload = () => setPreviewLoaded(true);
    img.onerror = () => setPreviewLoaded(false);
    img.src = displayPreviewUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [displayPreviewUrl]);

  const handleStartDrag = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Convert percentage position to pixels for drag start
    const currentX = (imagePosition.x / 100) * containerRect.width;
    const currentY = (imagePosition.y / 100) * containerRect.height;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - currentX,
      y: e.clientY - currentY
    });
  };

  const handleDrag = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Constrain the image within the container bounds
    const container = containerRef.current;
    const image = imageRef.current;
    if (container && image) {
      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      
      // Calculate bounds
      const minX = -(imageRect.width - containerRect.width);
      const minY = -(imageRect.height - containerRect.height);
      const maxX = 0;
      const maxY = 0;
      
      // Constrain position and convert to percentages
      const constrainedX = Math.min(maxX, Math.max(minX, newX));
      const constrainedY = Math.min(maxY, Math.max(minY, newY));
      
      // Convert to percentages
      const xPercent = (constrainedX / containerRect.width) * 100;
      const yPercent = (constrainedY / containerRect.height) * 100;
      
      setImagePosition({ x: xPercent, y: yPercent });
    }
  };

  const handleEndDrag = () => {
    setIsDragging(false);
  };

  const toggleAssetSelection = (asset) => {
    if (!asset?._id) return;
    setSelectedAssets((prev) => {
      const exists = prev.find((a) => a._id === asset._id);
      if (exists) return prev.filter((a) => a._id !== asset._id);
      if (prev.length >= 5) {
        setAssetError('You can select up to 5 assets for compositing');
        return prev;
      }
      return [...prev, asset];
    });
  };

  const handleZoom = (delta) => {
    setScale(prevScale => {
      const newScale = prevScale + delta;
      return Math.max(0.5, Math.min(3, newScale));
    });
  };

  // Qwen composite: use selected assets as references
  const handleGenerateQwen = async () => {
    const trimmedPrompt = qwenPrompt.trim();

    // Determine base image: use existing preview/base, otherwise first selected asset
    const baseImage = displayPreviewUrl || (selectedAssets[0]?.imageUrl || '');
    if (!baseImage && !trimmedPrompt) {
      setAssetError('Provide either a prompt or a base image before compositing');
      return;
    }

    if (!baseImage) {
      setAssetError('Base image required before compositing');
      return;
    }
    // Optimistically set base in editedFrame if not already present
    if (!displayPreviewUrl && baseImage) {
      resetImageTransform();
      setEditedFrame((prev) => ({ ...prev, imageUrl: baseImage, previewUrl: baseImage }));
    }

    setIsQwenGenerating(true);
    setAssetError(null);
    try {
      const ratioSelection = aiAspectRatio?.trim();
      const fetchToBlob = async (url) => {
        if (url?.startsWith('data:')) {
          const processed = ratioSelection ? await cropBase64ToRatio(url, ratioSelection) : url;
          return dataURLToBlob(processed);
        }
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`Failed to fetch image: ${resp.status}`);
        const blob = await resp.blob();
        if (!ratioSelection) return blob;
        const dataUrl = await blobToDataURL(blob);
        const processed = await cropBase64ToRatio(dataUrl, ratioSelection);
        return dataURLToBlob(processed);
      };

      const seedNum = computeSeed(qwenSeed);
      const references = selectedAssets.map((a) => a.imageUrl).filter(Boolean);

      const orderedRefs = [baseImage, ...references].filter(Boolean);
      if (orderedRefs.length === 0) {
        setAssetError('Base image required before compositing');
        return;
      }

      const primaryUrl = orderedRefs[0];
      const secondaryUrl = orderedRefs[1] || orderedRefs[0];
      const tertiaryUrl = orderedRefs[2] || orderedRefs[1] || orderedRefs[0];

      const uploadRefImage = async (url, idx) => {
        const blob = await fetchToBlob(url);
        return await comfyService.uploadImage(blob, `ref${idx}-${Date.now()}-${Math.floor(Math.random() * 1000)}.png`);
      };

      const [primaryName, secondaryName, tertiaryName] = await Promise.all([
        uploadRefImage(primaryUrl, 1),
        uploadRefImage(secondaryUrl, 2),
        uploadRefImage(tertiaryUrl, 3)
      ]);

      // Prepare overrides for prompt/seed and images
      const promptText = trimmedPrompt ? buildQwenPrompt() : '';
      const overrides = {
        '143:124': { prompt: promptText },
        '143:126': { prompt: promptText },
        '143:128': { seed: seedNum },
        '41': { image: primaryName },
        '83': { image: secondaryName },
        '144': { image: tertiaryName }
      };
      const imageBase64 = await comfyService.runWorkflow(DEFAULT_QWEN_EDIT_WORKFLOW, overrides);
      setEditedFrame(prev => ({ ...prev, previewUrl: imageBase64, imageUrl: imageBase64 }));
      resetImageTransform();
      setShowQwenDialog(false);
      setSelectedAssets([]);
      resetAiFields();
    } catch (err) {
      console.error('Qwen generate error', err);
      setAssetError(err.message || 'Failed to run Qwen composite');
    } finally {
      setIsQwenGenerating(false);
    }
  };

  const askUser = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        message,
        onConfirm: () => { setConfirmDialog({ open: false, message: '', onConfirm: null }); resolve(true); },
        onClose: () => { setConfirmDialog({ open: false, message: '', onConfirm: null }); resolve(false); }
      });
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      setSaveError(null);
      
      // Validate required fields
      if (!editedFrame.imageUrl && !editedFrame.previewUrl) {
        setSaveError('Image is required. Please upload an image or save a drawing for the frame.');
        return;
      }

      // Validate image data
      // Keep the original/base image when present; only fall back to preview if no base image exists
      const previewFallback = [displayPreviewUrl, editedFrame.previewUrl].find((v) => typeof v === 'string' && v) || null;
      let imageUrl = editedFrame.imageUrl || editedFrame.previewUrl;

      // Handle case where imageUrl might be an object (from database or accidental event)
      if (imageUrl && typeof imageUrl === 'object') {
        // If a DOM/React event was mistakenly stored, drop it and use preview fallback
        if (imageUrl.data && typeof imageUrl.data === 'string') {
          imageUrl = imageUrl.data;
        } else {
          console.error('Invalid imageUrl object (event detected):', imageUrl);
          imageUrl = null;
        }
      }
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        if (previewFallback) {
          imageUrl = previewFallback;
        } else {
          setSaveError('Image is required. Please upload an image.');
          return;
        }
      }
      
      const isServerUrl = imageUrl.startsWith('/') || imageUrl.startsWith('http');
      const isBase64 = imageUrl.startsWith('data:');
      if (!isServerUrl && !isBase64) {
        console.error('Invalid imageUrl string:', imageUrl);
        setSaveError('Image is required. Please upload or select an image again.');
        return;
      }

      // Check if this is a server URL (existing image) or base64 data (new/modified image)
      
      // For existing frames with server URLs, we can save without re-uploading the image
      // The server will keep the existing image if no new base64 data is provided
      let mimeType = null;
      let base64Data = null;
      
      if (isBase64) {
        // Validate base64 image format
        const matches = imageUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
        if (!matches || matches.length !== 3) {
          setSaveError('Invalid image data format. Please try uploading the image again.');
          return;
        }
        [, mimeType, base64Data] = matches;
        
        // Validate mime type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(mimeType)) {
          setSaveError(`Invalid image type. Allowed types are: ${allowedTypes.join(', ')}`);
          return;
        }

        // Validate base64 data
        try {
          const decoded = atob(base64Data);
          if (!decoded || decoded.length === 0) {
            throw new Error('Invalid or empty image data');
          }
          
          // Check if image size is within limits (10MB)
          const maxSize = 10 * 1024 * 1024; // 10MB
          const approxSize = Math.ceil(base64Data.length * 0.75);
          if (approxSize > maxSize) {
            throw new Error(`Image size exceeds maximum limit of 10MB. Current size: ${(approxSize / (1024 * 1024)).toFixed(2)}MB`);
          }
        } catch (e) {
          console.error('Base64 validation error:', e);
          setSaveError(`Invalid image data: ${e.message}`);
          return;
        }
      } else if (!isServerUrl) {
        setSaveError('Invalid image URL format.');
        return;
      }

      const sceneNumber = parseInt(editedFrame.sceneNumber) || 1;
      const shotNumber = parseInt(editedFrame.shotNumber) || 1;
      const updatedFrame = {
        ...editedFrame,
        sceneNumber,
        shotNumber,
        drawings: Array.isArray(editedFrame.drawings) ? editedFrame.drawings : [],
        layers: Array.isArray(editedFrame.layers) ? editedFrame.layers : [],
        currentLayerId: editedFrame.currentLayerId || null,
        imageUrl: imageUrl, // Use preview fallback if no base image
        previewUrl: editedFrame.previewUrl || null, // Explicitly include previewUrl for drawings
        notes: editedFrame.notes || '',
        imagePosition: {
          x: Number(imagePosition.x || 0),
          y: Number(imagePosition.y || 0)
        },
        imageScale: Number(scale || 1)
      };

      try {
        await onSave(updatedFrame);
        setSaveError(null);
        onClose();
      } catch (apiError) {
        console.error('API error when saving frame:', apiError);
        const result = await retrySaveWithShotNumber(updatedFrame, sceneNumber, shotNumber, apiError, onSave, askUser, null);
        if (result.success) {
          setSaveError(null);
          onClose();
        } else {
          setSaveError(result.error);
        }
      }
    } catch (error) {
      console.error('Error saving frame:', error);
      setSaveError(error.message || 'Failed to save frame. Please ensure all required fields are filled correctly.');
    } finally {
      setIsSaving(false);
    }
  };

  // Asset selection/generation
  const handleAssetSelect = (asset) => {
    if (!asset?.imageUrl) return;
    setEditedFrame(prev => ({
      ...prev,
      imageUrl: asset.imageUrl,
      previewUrl: asset.imageUrl
    }));
  };

  const handleGenerateAsset = async (saveToAssets = true) => {
    if (!genPrompt.trim()) {
      setAssetError('Prompt is required for generation');
      return;
    }
    setIsGenerating(true);
    setAssetError(null);
    setIsQwenGenerating(true);
    try {
      const seedNum = computeSeed(genSeed);
      const promptText = buildPromptWithMods(genPrompt);
      // Set dimensions based on selected aspect ratio (default 1024x1024) while preserving ratio
      const ratio = parseFloat(aiAspectRatio || '1');
      const baseSide = 1024;
      let width = baseSide;
      let height = baseSide;
      if (ratio && isFinite(ratio) && ratio > 0) {
        if (ratio >= 1) {
          width = baseSide;
          height = Math.round(baseSide / ratio);
        } else {
          height = baseSide;
          width = Math.round(baseSide * ratio);
        }
      }
      const clampDims = (w, h) => {
        const MAX = 1536;
        const MIN = 512;
        const scaleDown = Math.min(1, MAX / Math.max(w, h));
        w = Math.round(w * scaleDown);
        h = Math.round(h * scaleDown);
        const scaleUp = Math.max(1, MIN / Math.min(w, h));
        if (scaleUp > 1) {
          const safeUp = Math.min(scaleUp, MAX / Math.max(w, h));
          w = Math.round(w * safeUp);
          h = Math.round(h * safeUp);
        }
        return { width: w, height: h };
      };
      ({ width, height } = clampDims(width, height));
      const workflowOverrides = { ...DEFAULT_Z_IMAGE_WORKFLOW };
      if (workflowOverrides['57:13']) {
        workflowOverrides['57:13'] = {
          ...workflowOverrides['57:13'],
          inputs: {
            ...(workflowOverrides['57:13'].inputs || {}),
            width,
            height
          }
        };
      }
      const overrides = {
        '58': { value: promptText },
        '57:3': { seed: seedNum }
      };
      const imageBase64 = await comfyService.runWorkflow(workflowOverrides, overrides);
      if (saveToAssets) {
        const name = genName.trim() || genPrompt.slice(0, 40);
        await assetService.createAsset(projectId, {
          type: genType,
          name,
          description: genPrompt,
          imageBase64,
          metadata: { seed: seedNum, workflow: 'z-image' }
        });
        const data = await assetService.listAssets(projectId);
        setAssets(data || []);
      }
      setEditedFrame(prev => ({ ...prev, imageUrl: imageBase64, previewUrl: imageBase64 }));
      resetImageTransform();
      if (saveToAssets) {
        handleCloseQwenDialog();
      } else {
        setShowQwenDialog(false);
        resetAiFields();
      }
    } catch (err) {
      console.error('Generate asset error', err);
      setAssetError(err.message || 'Failed to generate asset');
    } finally {
      setIsGenerating(false);
      setIsQwenGenerating(false);
    }
  };

  const handleDrawingSave = (drawingData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Saving drawing data:', drawingData);
    }
    try {
      setIsSaving(true);
      setSaveError(null);

      // The DrawingCanvas has already prepared the image with drawings.
      // We just need to update the state with the new data.
      setEditedFrame(prev => {
        const compositePreview = [drawingData.previewUrl, drawingData.image, drawingData.data, prev?.previewUrl]
          .find((v) => typeof v === 'string' && v);
        const nextImageUrl = typeof prev?.imageUrl === 'string' && prev.imageUrl ? prev.imageUrl : compositePreview;
        return {
        ...prev,
        // Preserve the base image; if none exists, fall back to the new composite preview
        imageUrl: nextImageUrl || null,
        // Keep composite preview for thumbnail/display
        previewUrl: compositePreview || prev.previewUrl,
        // Keep drawings as vector/layer data to remain editable
        drawings: Array.isArray(drawingData.drawings) ? drawingData.drawings : (prev.drawings || []),
        // Persist layers and currentLayerId from the editor session
        layers: Array.isArray(drawingData.layers) ? drawingData.layers : (prev.layers || []),
        currentLayerId: drawingData.currentLayerId ?? prev.currentLayerId ?? null,
        // Update aspect ratio so preview and persistence reflect the canvas selection
        aspectRatio: drawingData.aspectRatio ?? prev.aspectRatio,
        aspectLabel: drawingData.aspectLabel ?? prev.aspectLabel,
        // Keep non-composited so editor continues to overlay drawings
        composited: false,
      };
      });

      // Return to the main modal view
      setIsDrawing(false);
    } catch (error) {
      console.error('Error in handleDrawingSave:', error);
      setSaveError(error.message || 'Failed to save drawing.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!frame && !editedFrame) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleDialogClose} 
        maxWidth="lg" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 1,
            overflow: 'hidden',
            width: '90vw',
            maxWidth: '1100px',
            height: 'auto',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme) => theme.palette.background.paper
          }
        }}
      >
        <DialogTitle 
          component="div"
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {isNewFrame ? 'New Frame' : 'Edit Frame'}
          </Typography>
          <IconButton 
            onClick={handleDialogClose} 
            size="small" 
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, flex: 1, overflowY: 'auto' }}>
          <Box sx={{ mb: 3 }}>
            {!isDrawing ? (
              <>
                {/* Error/Success Message */}
                {saveError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 1
                    }}
                  >
                    {saveError}
                  </Alert>
                )}
                
                {/* Frame Information Section - Minimal */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem'
                  }}>
                    Frame Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Scene"
                        value={editedFrame.sceneNumber || ''}
                        onChange={(e) => setEditedFrame({ 
                          ...editedFrame, 
                          sceneNumber: e.target.value.trim() 
                        })}
                        type="number"
                        inputProps={{ min: "1" }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Shot"
                        value={editedFrame.shotNumber || ''}
                        onChange={(e) => setEditedFrame({ 
                          ...editedFrame, 
                          shotNumber: e.target.value.trim() 
                        })}
                        type="number"
                        inputProps={{ min: "1" }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="shot-type-label">Shot Type</InputLabel>
                        <Select
                          labelId="shot-type-label"
                          value={editedFrame.shotType || 'medium-shot'}
                          label="Shot Type"
                          onChange={(e) => setEditedFrame({ 
                            ...editedFrame, 
                            shotType: e.target.value 
                          })}
                        >
                          {shotTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={2}
                        value={editedFrame.description || ''}
                        onChange={(e) => setEditedFrame({ ...editedFrame, description: e.target.value })}
                        placeholder="What happens in this shot..."
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Image Creation Options - Minimal */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem'
                  }}>
                    Add Image
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<BrushIcon />}
                      onClick={() => setIsDrawing(true)}
                      sx={{
                        borderRadius: 1,
                        px: 2.5,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      Draw
                    </Button>
                    
                    {FEATURE_FLAGS.storyboardCG && (
                      <Button
                        variant="outlined"
                        startIcon={<TimelineIcon />}
                        onClick={() => setShowCGScene(true)}
                        sx={{
                          borderRadius: 1,
                          px: 2.5,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: 'divider',
                          '&:hover': {
                            borderColor: 'secondary.main',
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.04)
                          }
                        }}
                      >
                        3D Scene
                      </Button>
                    )}
                    
                    <Box {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button
                        variant="outlined"
                        startIcon={<FileUploadIcon />}
                        component="span"
                        sx={{
                          borderRadius: 1,
                          px: 2.5,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: 'divider',
                          '&:hover': {
                            borderColor: 'success.main',
                            backgroundColor: (theme) => alpha(theme.palette.success.main, 0.04)
                          }
                        }}
                      >
                        Upload
                      </Button>
                    </Box>

                    {FEATURE_FLAGS.storyboardAI && (
                      <Button
                        variant="outlined"
                        startIcon={<MotionPhotosAutoIcon />}
                        onClick={() => {
                          onSetVariantOnly && onSetVariantOnly(false);
                          setShowQwenDialog(true);
                        }}
                        sx={{
                          borderRadius: 1,
                          px: 2.5,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: 'divider',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                          }
                        }}
                      >
                        AI
                      </Button>
                    )}

                  </Box>

                </Box>

                {Boolean(displayPreviewUrl) ? (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 2 
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.7rem'
                      }}>
                        Preview
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<BrushIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setIsDrawing(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        Edit Drawing
                      </Button>
                      <Box />
                    </Box>
                    
                    <Box
                      ref={containerRef}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: `${containerWidth}px`,
                        height: `${containerHeight}px`,
                        overflow: 'hidden',
                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                          ? 'rgba(0, 0, 0, 0.3)' 
                          : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        mx: 'auto',
                        cursor: isDragging ? 'grabbing' : 'grab'
                      }}
                      onMouseDown={handleStartDrag}
                      onMouseMove={handleDrag}
                      onMouseUp={handleEndDrag}
                      onMouseLeave={handleEndDrag}
                    >
                      {previewLoaded ? (
                        <img
                          ref={imageRef}
                          src={displayPreviewUrl}
                          alt="Storyboard frame"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: `${scale * 100}%`,
                            height: 'auto',
                            transform: `translate(${imagePosition.x}%, ${imagePosition.y}%) scale(${scale})`,
                            transformOrigin: '0 0',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                          }}
                          draggable="false"
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', width: '100%', py: 6 }}
                        >
                          Loading preview...
                        </Typography>
                      )}

                      {/* Image Controls */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          right: 12,
                          display: 'flex',
                          gap: 0.5,
                          backgroundColor: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(30, 30, 30, 0.85)' 
                            : 'rgba(255, 255, 255, 0.85)',
                          borderRadius: 1,
                          padding: '6px',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Tooltip title="Zoom in">
                          <IconButton 
                            size="small" 
                            onClick={() => handleZoom(0.1)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Zoom out">
                          <IconButton 
                            size="small" 
                            onClick={() => handleZoom(-0.1)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <ZoomOutIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Reset view">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setScale(1);
                              setImagePosition({ x: 0, y: 0 });
                            }}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      {/* Drag Indicator */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          borderRadius: 1,
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          opacity: 0.7
                        }}
                      >
                        <OpenWithIcon fontSize="small" />
                        <Typography variant="caption">Drag to position</Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', width: '100%', py: 2 }}
                  >
                    Upload an image to see the preview here.
                  </Typography>
                )}
              </>
            ) : (
              <DrawingCanvas
                frame={{
                  ...editedFrame,
                  // Ensure imageUrl is passed correctly
                  imageUrl: editedFrame.imageUrl || '',
                  // Always pass drawings so editor preserves overlay/layers even if previously composited
                  drawings: editedFrame?.drawings || [],
                  // Preserve layers/currentLayerId for the editor session
                  layers: editedFrame?.layers || [],
                  currentLayerId: editedFrame?.currentLayerId || null,
                  // Treat session as non-composited to allow new strokes to remain visible
                  composited: false
                }}
                onSave={handleDrawingSave}
                onClose={() => setIsDrawing(false)}
                aspectRatio={editedFrame?.aspectRatio || '16/9'}
              />
            )}
          </Box>
        </DialogContent>
        {/* saveError is already shown as Alert inside DialogContent above */}
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleDialogClose} sx={{ textTransform: 'none', borderRadius: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !(editedFrame.imageUrl || editedFrame.previewUrl)}
            sx={{
              borderRadius: 1,
              px: 3,
              py: 1,
              textTransform: 'none'
            }}
          >
            {isSaving ? 'Saving...' : 'Save Frame'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI (Qwen) dialog for composite/variation */}
      <Dialog
        open={showQwenDialog}
        onClose={handleCloseQwenDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AI Generate / Compose</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={qwenMode === 'z-image' ? 'contained' : 'outlined'}
              onClick={() => setQwenMode('z-image')}
              size="small"
              disabled={variantOnly}
              title={variantOnly ? 'Only composite is available in variant-only mode' : ''}
            >
              Generate (z-image)
            </Button>
            <Button
              variant={qwenMode === 'composite' ? 'contained' : 'outlined'}
              onClick={() => setQwenMode('composite')}
              size="small"
            >
              Composite (Qwen)
            </Button>
          </Box>

          {displayPreviewUrl && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={displayPreviewUrl}
                  alt="Base reference"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Base image in use
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Using current frame preview (duplicated image)
                </Typography>
              </Box>
            </Box>
          )}

          {qwenMode === 'z-image' && !variantOnly ? (
            <QwenFormFields
              prompt={genPrompt}
              onPromptChange={setGenPrompt}
              seed={genSeed}
              onSeedChange={setGenSeed}
              autoRandomSeed={autoRandomSeed}
              onAutoRandomSeedChange={setAutoRandomSeed}
              variationStrength={variationStrength}
              onVariationStrengthChange={setVariationStrength}
              shotSizePrompt={shotSizePrompt}
              onShotSizeChange={handleShotSizeChange}
              lensPrompt={lensPrompt}
              onLensChange={setLensPrompt}
              anglePrompt={anglePrompt}
              onAngleChange={setAnglePrompt}
              aiAspectRatio={aiAspectRatio}
              onAspectRatioChange={setAiAspectRatio}
              aiTimeOfDay={aiTimeOfDay}
              onTimeOfDayChange={setAiTimeOfDay}
              aiAtmosphere={aiAtmosphere}
              onAtmosphereChange={setAiAtmosphere}
              aiLightingStyle={aiLightingStyle}
              onLightingChange={setAiLightingStyle}
              aiCompositionStyle={aiCompositionStyle}
              onCompositionChange={handleCompositionChange}
              aiCompositionFocus={aiCompositionFocus}
              onCompositionFocusChange={handleCompositionFocusChange}
              shotSizeOptions={shotSizeOptions}
              lensOptions={lensOptions}
              angleOptions={angleOptions}
              aspectRatioOptions={aspectRatioOptions}
              timeOfDayOptions={timeOfDayOptions}
              atmosphereOptions={atmosphereOptions}
              lightingOptions={lightingOptions}
              compositionOptions={compositionOptions}
              ruleOfThirdsFocusLabelMap={ruleOfThirdsFocusLabelMap}
              ruleOfThirdsGridCells={ruleOfThirdsGridCells}
            />
          ) : (
          <>
            <QwenFormFields
              prompt={qwenPrompt}
              onPromptChange={setQwenPrompt}
              seed={qwenSeed}
              onSeedChange={setQwenSeed}
              autoRandomSeed={autoRandomSeed}
              onAutoRandomSeedChange={setAutoRandomSeed}
              variationStrength={variationStrength}
              onVariationStrengthChange={setVariationStrength}
              shotSizePrompt={shotSizePrompt}
              onShotSizeChange={handleShotSizeChange}
              lensPrompt={lensPrompt}
              onLensChange={setLensPrompt}
              anglePrompt={anglePrompt}
              onAngleChange={setAnglePrompt}
              aiAspectRatio={aiAspectRatio}
              onAspectRatioChange={setAiAspectRatio}
              aiTimeOfDay={aiTimeOfDay}
              onTimeOfDayChange={setAiTimeOfDay}
              aiAtmosphere={aiAtmosphere}
              onAtmosphereChange={setAiAtmosphere}
              aiLightingStyle={aiLightingStyle}
              onLightingChange={setAiLightingStyle}
              aiCompositionStyle={aiCompositionStyle}
              onCompositionChange={handleCompositionChange}
              aiCompositionFocus={aiCompositionFocus}
              onCompositionFocusChange={handleCompositionFocusChange}
              shotSizeOptions={shotSizeOptions}
              lensOptions={lensOptions}
              angleOptions={angleOptions}
              aspectRatioOptions={aspectRatioOptions}
              timeOfDayOptions={timeOfDayOptions}
              atmosphereOptions={atmosphereOptions}
              lightingOptions={lightingOptions}
              compositionOptions={compositionOptions}
              ruleOfThirdsFocusLabelMap={ruleOfThirdsFocusLabelMap}
              ruleOfThirdsGridCells={ruleOfThirdsGridCells}
              promptLabel="Prompt"
              promptPlaceholder="Describe the composite (style, lighting, action)"
            />

            {FEATURE_FLAGS.storyboardAssets && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Select up to 5 assets</Typography>
                {isLoadingAssets ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {groupedAssets.map((group) => (
                      <Accordion key={group.key} defaultExpanded={false} disableGutters sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{group.label}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={1}>
                            {group.items.map((asset) => {
                              const selected = selectedAssets.some((a) => a._id === asset._id);
                              return (
                                <Grid item xs={6} sm={4} md={3} key={asset._id}>
                                  <Card
                                    onClick={() => toggleAssetSelection(asset)}
                                    sx={{
                                      cursor: 'pointer',
                                      borderColor: selected ? 'primary.main' : 'divider',
                                      borderWidth: selected ? 2 : 1,
                                      borderStyle: 'solid'
                                    }}
                                  >
                                    <CardActionArea>
                                      <Avatar
                                        variant="rounded"
                                        src={asset.imageUrl}
                                        alt={asset.name}
                                        sx={{ width: '100%', height: 96, borderRadius: 0 }}
                                      />
                                      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography variant="caption" noWrap>{asset.name || 'Untitled'}</Typography>
                                        {selected && <CheckIcon fontSize="small" color="primary" />}
                                      </Box>
                                    </CardActionArea>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
          )}
          {isQwenGenerating && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.72)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                zIndex: 2
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">Generating...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQwenDialog}>Cancel</Button>
          {qwenMode === 'z-image' ? (
            <Button
              variant="contained"
              onClick={() => handleGenerateAsset(false)}
              disabled={!!assetError || isQwenGenerating}
            >
              {isQwenGenerating ? <CircularProgress size={18} /> : 'Generate'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => { setQwenMode('composite'); handleGenerateQwen(); }}
              disabled={isQwenGenerating}
            >
              {isQwenGenerating ? <CircularProgress size={18} /> : 'Composite'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {showCGScene && (
        <ThreeDScene 
          open={showCGScene} 
          onClose={() => setShowCGScene(false)} 
          aspectRatio={parseAspectRatio(editedFrame.aspectRatio) || parseAspectRatio(defaultAspectRatio) || 1.78}
          onExportImage={(png, sceneState) => {
            if (!png) return;
            setEditedFrame(prev => ({
              ...prev,
              previewUrl: png,
              scene3d: sceneState,
              aspectRatio: parseAspectRatio(prev.aspectRatio) || parseAspectRatio(defaultAspectRatio) || 1.78
            }));
            setShowCGScene(false);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={confirmDialog.onClose}
        confirmLabel="Use suggested"
      />
    </>
  );
};

const Storyboard = () => {
  const { projectId } = useParams();
  const [frames, setFrames] = useState([]);
  // Keep a ref to frames so callbacks (renderFrame, moveCard) don't need frames in their deps
  const framesRef = useRef(frames);
  useEffect(() => { framesRef.current = frames; }, [frames]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({ visible: false, message: '', type: '' });
  const [lastSaved, setLastSaved] = useState(null);
  const [defaultAspectRatio, setDefaultAspectRatio] = useState('1.78'); // 16:9 widescreen format
  const [isUpdatingAspectRatio, setIsUpdatingAspectRatio] = useState(false);
  const [aspectRatioMenuAnchorEl, setAspectRatioMenuAnchorEl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const moveTimeoutRef = useRef(null);
  const lastSuccessfulOrder = useRef(null);
  // Asset library (separate from frame dialog)
  const [assetLibraryOpen, setAssetLibraryOpen] = useState(false);
  const [assetLibraryLoading, setAssetLibraryLoading] = useState(false);
  const [assetLibraryError, setAssetLibraryError] = useState('');
  const [assetLibrary, setAssetLibrary] = useState([]);
  const [assetLibPrompt, setAssetLibPrompt] = useState('');
  const [assetLibSeed, setAssetLibSeed] = useState('0');
  const [assetLibType, setAssetLibType] = useState('character');
  const [assetLibFilter, setAssetLibFilter] = useState('all');
  const [assetLibName, setAssetLibName] = useState('');
  const [assetLibGenerating, setAssetLibGenerating] = useState(false);
  const [autoRandomizeSeed, setAutoRandomizeSeed] = useState(false);
  const [variation, setVariation] = useState(0.5);
  const [pendingAssetForFrame, setPendingAssetForFrame] = useState(null);
  const [assetPreview, setAssetPreview] = useState({ open: false, asset: null });
  const [shouldAutoOpenQwen, setShouldAutoOpenQwen] = useState(false);
  const [variantOnly, setVariantOnly] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const askUser = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        message,
        onConfirm: () => { setConfirmDialog({ open: false, message: '', onConfirm: null }); resolve(true); },
        onClose: () => { setConfirmDialog({ open: false, message: '', onConfirm: null }); resolve(false); }
      });
    });
  }, []);

  const aspectRatios = [
    { value: '1.78', label: '1.78:1 (16:9 HD) - Default Widescreen' },
    { value: '1.85', label: '1.85:1 (US Widescreen)' },
    { value: '2.35', label: '2.35:1 (CinemaScope)' },
    { value: '2.39', label: '2.39:1 (Anamorphic Widescreen)' },
    { value: '1.66', label: '1.66:1 (European Widescreen)' },
    { value: '1.43', label: '1.43:1 (IMAX)' },
    { value: '1.33', label: '1.33:1 (4:3 Academy)' }
  ];

  const handleAspectRatioChange = async (e) => {
    const newRatio = e.target.value;
    setDefaultAspectRatio(newRatio);
    setIsUpdatingAspectRatio(true);

    try {
      // Get fresh data first
      const currentFrames = await storyboardService.getProjectStoryboards(projectId);
      if (!currentFrames) {
        throw new Error('Failed to fetch current frames');
      }

      // Create a copy of frames with updated aspect ratios
      const updatedFrames = currentFrames.map(frame => ({
        ...frame,
        aspectRatio: newRatio
      }));

      // Update local state immediately for responsive UI
      setFrames(updatedFrames);

      // Update the backend sequentially to avoid race conditions on failure
      for (const frame of updatedFrames) {
        try {
          await storyboardService.updateStoryboard(projectId, frame);
        } catch (err) {
          console.error(`Error updating frame ${frame._id}:`, err);
          throw err;
        }
      }

      // Fetch final state to ensure consistency
      const finalFrames = await storyboardService.getProjectStoryboards(projectId);
      if (finalFrames) {
        setFrames(finalFrames);
      }

      setError({ visible: false, message: '', type: '' });
    } catch (err) {
      console.error('Error updating aspect ratios:', err);
      setError({ visible: true, message: err.message || 'Failed to update aspect ratios', type: 'error' });
      
      // Revert the changes if there was an error
      const response = await storyboardService.getProjectStoryboards(projectId);
      if (response) {
        setFrames(response);
      }
    } finally {
      setIsUpdatingAspectRatio(false);
    }
  };

  const isAspectRatioMenuOpen = Boolean(aspectRatioMenuAnchorEl);
  const handleOpenAspectRatioMenu = (e) => {
    setAspectRatioMenuAnchorEl(e.currentTarget);
  };
  const handleCloseAspectRatioMenu = () => {
    setAspectRatioMenuAnchorEl(null);
  };
  const handleSelectAspectRatio = async (newRatio) => {
    await handleAspectRatioChange({ target: { value: newRatio } });
    handleCloseAspectRatioMenu();
  };

  const handleSelectAssetForFrame = useCallback((asset) => {
    if (!asset) return;
    setPendingAssetForFrame(asset);
    if (!editDialogOpen) {
      setEditDialogOpen(true);
    }
  }, [editDialogOpen]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxSize: 10485760, // 10MB
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setError({ visible: true, message: 'Some files were rejected. Please ensure files are images under 10MB.', type: 'error' });
        return;
      }

      for (const file of acceptedFiles) {
        try {
          setLoading(true);
          const imageData = await readFileAsDataURL(file);
          const img = await loadImageElement(imageData);

          // Check if image dimensions are reasonable
          if (img.width < 50 || img.height < 50) {
            throw new Error('Image dimensions are too small. Minimum size is 50x50 pixels.');
          }

          await handleAddFrame(imageData);
          setError({ visible: false, message: '', type: '' });
        } catch (err) {
          console.error('Error processing image:', err);
          setError({ visible: true, message: err.message || 'Failed to process image', type: 'error' });
        } finally {
          setLoading(false);
        }
      }
    }
  });

  // Track the initial and final positions for drag operations
  const dragState = useRef({
    isDragging: false,
    initialIndex: -1,
    currentIndex: -1,
    originalFrames: [],
    pendingUpdate: false,
    dropHandled: false,
    draggedId: null
  });

  const duplicateFrame = useCallback(async (frameToCopy) => {
    try {
      const nextOrder = frames.length > 0 ? Math.max(...frames.map(f => f.order || 0)) + 1 : 1;
      const nextShot = (Number(frameToCopy.shotNumber) || 0) + 1;

      // Prefer base64 imageUrl/previewUrl; if neither exists, bail with a friendly error
      const imageSource = frameToCopy.previewUrl || frameToCopy.imageUrl;
      if (!imageSource) {
        throw new Error('No image to duplicate. Please add or upload an image first.');
      }

      const toDataUrl = async (src) => {
        if (typeof src !== 'string') return null;
        if (src.startsWith('data:')) return src;
        try {
          const resp = await fetch(src);
          const blob = await resp.blob();
          return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.error('Failed to fetch image for duplication:', e);
          return null;
        }
      };

      const dataUrlImage = await toDataUrl(imageSource);
      if (!dataUrlImage) {
        throw new Error('Unable to duplicate image. Please open the frame and re-save the image first.');
      }

      const payload = {
        projectId,
        imageUrl: dataUrlImage,
        previewUrl: dataUrlImage,
        description: frameToCopy.description || '',
        notes: frameToCopy.notes || '',
        sceneNumber: frameToCopy.sceneNumber,
        shotNumber: nextShot,
        cameraAngle: frameToCopy.cameraAngle || 'Other',
        movement: frameToCopy.movement || 'Static',
        duration: frameToCopy.duration || 0,
        drawings: Array.isArray(frameToCopy.drawings) ? frameToCopy.drawings : [],
        layers: Array.isArray(frameToCopy.layers) ? frameToCopy.layers : [],
        currentLayerId: frameToCopy.currentLayerId || null,
        aspectRatio: frameToCopy.aspectRatio || defaultAspectRatio,
        order: nextOrder,
        imagePosition: frameToCopy.imagePosition || { x: 0, y: 0 },
        imageScale: frameToCopy.imageScale || 1,
      };

      const created = await storyboardService.createStoryboard(projectId, payload);
      if (created) {
        const updatedFrames = await storyboardService.getProjectStoryboards(projectId);
        if (updatedFrames) setFrames(updatedFrames);
        // Prefer the freshly saved frame from server list to ensure _id and persisted fields
        const saved = (updatedFrames || []).find(f => f._id === created._id) || created;
        setSelectedFrame(saved);
        setCurrentFrame(saved);
        setError({ visible: true, message: 'Frame duplicated', type: 'success' });
      }
    } catch (err) {
      console.error('Error duplicating frame:', err);
      setError({ visible: true, message: err.message || 'Failed to duplicate frame', type: 'error' });
    }
  }, [frames, projectId, defaultAspectRatio]);

  const loadAssetLibrary = useCallback(async () => {
    try {
      setAssetLibraryLoading(true);
      setAssetLibraryError('');
      const data = await assetService.listAssets(projectId);
      setAssetLibrary(data || []);
    } catch (err) {
      console.error('Error loading assets', err);
      setAssetLibraryError(err.message || 'Failed to load assets');
    } finally {
      setAssetLibraryLoading(false);
    }
  }, [projectId]);

  const handleGenerateAssetLibrary = useCallback(async () => {
    const basePrompt = assetLibPrompt.trim();
    if (!basePrompt && assetLibType !== 'character') {
      setAssetLibraryError('Prompt is required');
      return;
    }
    setAssetLibGenerating(true);
    setAssetLibraryError('');
    try {
      const seedNum = parseInt(assetLibSeed, 10) || 0;
      const characterGuidance = 'Full body hero character standing in a neutral T-pose, arms extended horizontally; perfectly centered, facing camera, evenly lit, on pure white seamless background, no shadows, no props, no environment, alpha-ready cutout look.';
      const finalPrompt = assetLibType === 'character'
        ? [characterGuidance, basePrompt].filter(Boolean).join('\n')
        : basePrompt;
      const overrides = {
        '58': { value: finalPrompt },
        '57:3': { seed: seedNum }
      };
      const imageBase64 = await comfyService.runWorkflow(DEFAULT_Z_IMAGE_WORKFLOW, overrides);
      const name = assetLibName.trim() || assetLibPrompt.slice(0, 40);
      await assetService.createAsset(projectId, {
        type: assetLibType,
        name,
        description: finalPrompt,
        imageBase64,
        metadata: { seed: seedNum, workflow: 'z-image' }
      });
      await loadAssetLibrary();
      setAssetLibPrompt('');
      setAssetLibName('');
    } catch (err) {
      console.error('Asset library generation error', err);
      setAssetLibraryError(err.message || 'Failed to generate asset');
    } finally {
      setAssetLibGenerating(false);
    }
  }, [assetLibPrompt, assetLibSeed, assetLibName, assetLibType, projectId, loadAssetLibrary]);

  useEffect(() => {
    if (assetLibraryOpen) {
      loadAssetLibrary();
      setAssetLibraryError('');
    }
  }, [assetLibraryOpen, loadAssetLibrary]);

  // Helper function to recalculate shot numbers within each scene after reordering
  // This ensures the chip (e.g., S1.1, S1.2) reflects the actual position
  const recalculateShotNumbers = useCallback((framesArray) => {
    // Group frames by scene number
    const sceneGroups = {};
    framesArray.forEach((frame, idx) => {
      const sceneNum = frame.sceneNumber || 1;
      if (!sceneGroups[sceneNum]) {
        sceneGroups[sceneNum] = [];
      }
      sceneGroups[sceneNum].push({ frame, originalIndex: idx });
    });

    // Recalculate shot numbers within each scene based on array order
    const updatedFrames = [...framesArray];
    Object.keys(sceneGroups).forEach(sceneNum => {
      const sceneFrames = sceneGroups[sceneNum];
      // Sort by original index to maintain array order within scene
      sceneFrames.sort((a, b) => a.originalIndex - b.originalIndex);
      // Assign sequential shot numbers
      sceneFrames.forEach((item, shotIdx) => {
        updatedFrames[item.originalIndex] = {
          ...updatedFrames[item.originalIndex],
          shotNumber: shotIdx + 1
        };
      });
    });

    return updatedFrames;
  }, []);

  // Server update function - only called at the end of drag operations
  const serverUpdate = useCallback(async (finalFrames, sourceIndex, destinationIndex, originalFrames) => {
    try {
      // Performance logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('FINAL Reordering storyboards:', {
          projectId,
          sourceIndex,
          destinationIndex,
          frameCount: finalFrames.length
        });
      }

      // Prepare reorder data with the final positions
      const reorderData = {
        sourceIndex,
        destinationIndex,
        frameIds: finalFrames.map(frame => frame._id)
      };

      // Send update to server
      const updatedFrames = await storyboardService.reorderStoryboards(projectId, reorderData);
      
      // Debug: Log server response
      if (process.env.NODE_ENV === 'development') {
        console.log('Server response:', updatedFrames);
        console.log('Is array:', Array.isArray(updatedFrames));
        console.log('Length:', updatedFrames?.length);
      }
      
      // Update state with server response if successful
      if (updatedFrames && Array.isArray(updatedFrames)) {
        // Debug: Log the merging process
        if (process.env.NODE_ENV === 'development') {
          console.log('finalFrames before merge:', finalFrames);
          console.log('updatedFrames from server:', updatedFrames);
        }
        
        // Helper to merge preserving a given array order
        const mergeWithOrder = (orderedFramesArr) => orderedFramesArr.map((updatedFrame, idx) => {
          const existingFrame = finalFrames.find(f => f._id === updatedFrame._id);
          const merged = {
            ...existingFrame,
            ...updatedFrame,
            imageUrl: existingFrame?.imageUrl || updatedFrame.imageUrl,
            order: typeof updatedFrame.order === 'number' ? updatedFrame.order : idx
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Merging frame:', { existing: existingFrame?._id, updated: updatedFrame._id, order: updatedFrame.order });
          }
          
          return merged;
        });

        // Prefer server-provided array order as canonical
        let mergedFrames = mergeWithOrder(updatedFrames);

        // Detect a potential backend no-op: server order equals original, but client final differs
        const idsEqual = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);
        const originalIds = (originalFrames || []).map(f => f._id);
        const serverIds = updatedFrames.map(f => f._id);
        const clientFinalIds = finalFrames.map(f => f._id);
        if (originalFrames && idsEqual(serverIds, originalIds) && !idsEqual(clientFinalIds, originalIds)) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Server returned original order; falling back to client final order');
          }
          mergedFrames = mergeWithOrder(finalFrames.map(f => ({ _id: f._id, order: f.order })));
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Final merged frames order:', mergedFrames.map(f => ({ id: f._id?.slice(-4), order: f.order })));
          console.log('Before sort - frame order:', mergedFrames.map(f => f._id?.slice(-4)));
        }
        
        // Force a complete re-render by creating a completely new array
        // Recalculate shot numbers to reflect new positions
        const freshFrames = recalculateShotNumbers(mergedFrames.map(frame => ({ ...frame })));
        setFrames(freshFrames);

        // Show discreet success message
        setError({ visible: true, message: 'Order saved', type: 'success' });

        // Clear success message after 1.5 seconds
        setTimeout(() => {
          setError({ visible: false, message: '', type: '' });
        }, 1500);
      } else if (updatedFrames && typeof updatedFrames === 'object') {
        // Server responded with a write result / non-array; treat as success and keep client order
        if (process.env.NODE_ENV === 'development') {
          console.warn('Non-array reorder response; keeping client order', updatedFrames);
        }
        const freshFrames = recalculateShotNumbers(finalFrames.map(f => ({ ...f })));
        setFrames(freshFrames);
        setError({ visible: true, message: 'Order saved', type: 'success' });
        setTimeout(() => setError({ visible: false, message: '', type: '' }), 1500);
      } else {
        console.error('Invalid response from server:', updatedFrames);
        // Keep client order to avoid jarring revert; notify user
        const freshFrames = recalculateShotNumbers(finalFrames.map(f => ({ ...f })));
        setFrames(freshFrames);
        setError({
          visible: true,
          message: 'Saved locally. Server response unexpected; will retry.',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error reordering frames:', error);
      // Keep client order to avoid jarring revert; inform user
      const freshFrames = recalculateShotNumbers(finalFrames.map(f => ({ ...f })));
      setFrames(freshFrames);
      setError({
        visible: true,
        message: 'Saved locally. Network/server issue; will retry.',
        type: 'warning'
      });
    } finally {
      // Reset drag state
      dragState.current = {
        isDragging: false,
        initialIndex: -1,
        currentIndex: -1,
        originalFrames: [],
        pendingUpdate: false,
        dropHandled: false,
        draggedId: null
      };
      setIsDragging(false);
    }
  }, [projectId, recalculateShotNumbers]);

  // Memoize the last move operation to prevent redundant moves
  const lastMoveOp = useRef({ dragIndex: -1, hoverIndex: -1 });

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    try {
      // Skip if indices are the same or if this is a duplicate of the last move
      if (
        dragIndex === hoverIndex ||
        (lastMoveOp.current.dragIndex === dragIndex && lastMoveOp.current.hoverIndex === hoverIndex)
      ) {
        return;
      }

      // Update last move operation
      lastMoveOp.current = { dragIndex, hoverIndex };

      if (process.env.NODE_ENV === 'development') {
        console.log('Moving card:', { dragIndex, hoverIndex });
      }

      // Update the current position - used by end() fallback
      if (dragState?.current) dragState.current.currentIndex = hoverIndex;

      // Use functional update to avoid stale closures
      setFrames(prev => {
        const base = dragState.current?.currentFrames ? [...dragState.current.currentFrames] : [...prev];

        // Recompute actual drag index using draggedId to prevent stale index issues
        const draggedId = dragState.current?.draggedId;
        let actualDragIndex = dragIndex;
        if (draggedId) {
          const foundIdx = base.findIndex(f => f._id === draggedId);
          if (foundIdx !== -1) actualDragIndex = foundIdx;
        }

        if (actualDragIndex === hoverIndex) return base; // no-op after recompute

        const next = [...base];
        const [movedFrame] = next.splice(actualDragIndex, 1);
        next.splice(hoverIndex, 0, movedFrame);

        // Recalculate shot numbers to reflect new positions
        const recalculated = recalculateShotNumbers(next);

        // Persist for end() and drop handlers
        if (dragState?.current) dragState.current.currentFrames = recalculated;
        lastSuccessfulOrder.current = recalculated.map(frame => frame._id);

        if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
        return recalculated;
      });
    } catch (err) {
      console.error('Unexpected error in moveCard:', err);
      setIsDragging(false);
      if (dragState?.current) dragState.current.isDragging = false;
    }
  }, [recalculateShotNumbers]);
  const handleUpdateFrame = useCallback(async (updatedFrame) => {
    try {
      const response = await storyboardService.updateStoryboard(projectId, updatedFrame);
      if (response) {
        setFrames(prevFrames => 
          prevFrames.map(frame => 
            frame._id === updatedFrame._id ? response : frame
          )
        );
        setError({ visible: false, message: '', type: '' });
      }
    } catch (error) {
      console.error('Error updating frame:', error);
      setError({
        visible: true,
        message: 'Failed to update frame. Please try again.',
        type: 'error'
      });
    }
  }, [projectId]);

  const handleDeleteFrame = useCallback(async (frameToDelete) => {
    try {
      await storyboardService.deleteStoryboard(projectId, frameToDelete._id);
      setFrames(prevFrames => prevFrames.filter(frame => frame._id !== frameToDelete._id));
      setError({ visible: false, message: '', type: '' });
    } catch (error) {
      console.error('Error deleting frame:', error);
      setError({
        visible: true,
        message: 'Failed to delete frame. Please try again.',
        type: 'error'
      });
    }
  }, [projectId]);

  const renderFrame = useCallback(({ frame, index }) => {
    const handleFrameClick = () => {
      setSelectedFrame(frame);
      setCurrentFrame(frame);
      setEditDialogOpen(true);
    };

    const handleDeleteClick = (e) => {
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }
      handleDeleteFrame(frame);
    };

    const handleDuplicateClick = (e) => {
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }
      duplicateFrame(frame);
    };

    return (
      <StoryboardFrame
        key={frame._id}
        frame={frame}
        index={index}
        moveCard={moveCard}
        setIsDragging={setIsDragging}
        isDragging={isDragging}
        frames={framesRef.current}
        dragState={dragState}
        serverUpdate={serverUpdate}
        isSelected={selectedFrame?._id === frame._id}
        onClick={handleFrameClick}
        onDelete={handleDeleteClick}
        onDuplicate={(e) => {
          if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
          duplicateFrame(frame);
        }}
        projectId={projectId}
        setError={setError}
        setLastSaved={setLastSaved}
      />
    );
  }, [selectedFrame, moveCard, isDragging, dragState, serverUpdate, duplicateFrame, projectId, setError, setLastSaved]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, []);

  const handleDeleteAssetLibrary = useCallback(async (assetId) => {
    try {
      await assetService.deleteAsset(projectId, assetId);
      await loadAssetLibrary();
    } catch (err) {
      console.error('Error deleting asset', err);
      setAssetLibraryError(err.message || 'Failed to delete asset');
    }
  }, [projectId, loadAssetLibrary]);

  // Fetch storyboards when component mounts
  useEffect(() => {
    let isMounted = true;

    const fetchStoryboards = async () => {
      try {
        setLoading(true);
        const data = await storyboardService.getProjectStoryboards(projectId);
        
        if (isMounted) {
          if (data && Array.isArray(data)) {
            setFrames(data);
          } else {
            setFrames([]);
          }
          setError({ visible: false, message: '', type: '' });
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching storyboards:', err);
          setError({ visible: true, message: 'Failed to load storyboards', type: 'error' });
          setFrames([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStoryboards();

    // Post-layout wake-up: ensure drop targets have correct rects on first paint
    const rafId = requestAnimationFrame(() => {
      setFrames(prev => [...prev]);
    });

    // Cleanup function
    return () => {
      isMounted = false;
      cancelAnimationFrame(rafId);
    };
  }, [projectId]); // Only re-run if projectId changes

  const handleAddFrame = async (imageUrl = null) => {
    const newFrame = {
      projectId,
      imageUrl: imageUrl || null,
      order: frames.length + 1,
      sceneNumber: frames.length + 1,
      shotNumber: 1,
      description: 'New Frame',
      notes: '',
      cameraAngle: 'Other',
      movement: 'Static',
      duration: 0,
      drawings: [],
      aspectRatio: parseAspectRatio(defaultAspectRatio) 
    };

    setCurrentFrame(newFrame);
    setEditDialogOpen(true);
  };

  const handleSaveFrame = async (frameData) => {
    try {
      // Validate required fields
      if (!frameData.imageUrl) {
        throw new Error('Image is required. Please upload an image for the frame.');
      }

      // Validate image data
      const imageUrl = frameData.imageUrl;
      
      // Check if this is a server URL (existing image) or base64 data (new/modified image)
      const isServerUrl = imageUrl.startsWith('/') || imageUrl.startsWith('http');
      const isBase64 = imageUrl.startsWith('data:');
      
      // For existing frames with server URLs, allow saving without re-uploading
      if (isBase64) {
        // Validate base64 image format
        const matches = imageUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
        if (!matches || matches.length !== 3) {
          throw new Error('Invalid image data format. Please try uploading the image again.');
        }
        const [, mimeType, base64Data] = matches;
        
        // Validate mime type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(mimeType)) {
          throw new Error(`Invalid image type. Allowed types are: ${allowedTypes.join(', ')}`);
        }

        // Validate base64 data
        try {
          const decoded = atob(base64Data);
          if (!decoded || decoded.length === 0) {
            throw new Error('Invalid or empty image data');
          }
          
          // Check if image size is within limits (10MB)
          const maxSize = 10 * 1024 * 1024; // 10MB
          const approxSize = Math.ceil(base64Data.length * 0.75);
          if (approxSize > maxSize) {
            throw new Error(`Image size exceeds maximum limit of 10MB. Current size: ${(approxSize / (1024 * 1024)).toFixed(2)}MB`);
          }
        } catch (e) {
          console.error('Base64 validation error:', e);
          throw new Error(`Invalid image data: ${e.message}`);
        }
      } else if (!isServerUrl) {
        throw new Error('Invalid image URL format.');
      }

      const sceneNumber = parseInt(frameData.sceneNumber) || 1;
      const shotNumber = parseInt(frameData.shotNumber) || 1;
      const description = frameData.description?.trim();

      // Get the next order number if this is a new frame
      const nextOrder = frameData._id ? frameData.order : (frames.length > 0 ? Math.max(...frames.map(f => f.order)) + 1 : 1);

      const updatedFrame = {
        ...frameData,
        _id: frameData._id, // Ensure _id is present for updates
        projectId: projectId,
        description: description || '', // Ensure description is never null
        sceneNumber: sceneNumber,
        shotNumber: shotNumber,
        order: nextOrder,
        aspectRatio: parseAspectRatio(frameData.aspectRatio) || defaultAspectRatio,
        composited: Boolean(frameData.composited),
        duration: parseFloat(frameData.duration) || 0,
        cameraAngle: frameData.cameraAngle || 'Other',
        movement: frameData.movement || 'Static',
        drawings: Array.isArray(frameData.drawings) ? frameData.drawings : [],
        layers: Array.isArray(frameData.layers) ? frameData.layers : [],
        currentLayerId: frameData.currentLayerId || null,
        // Persist previewUrl so composited drawing remains visible on reload
        previewUrl: frameData.previewUrl || null,
        imageUrl: frameData.imageUrl, // Keep imageUrl for service to handle
        notes: frameData.notes || '',
        imagePosition: {
          x: Number(frameData.imagePosition?.x || 0),
          y: Number(frameData.imagePosition?.y || 0)
        },
        imageScale: Number(frameData.imageScale || 1)
      };

      let response;
      try {
        if (updatedFrame._id) {
          response = await storyboardService.updateStoryboard(projectId, updatedFrame);
        } else {
          response = await storyboardService.createStoryboard(projectId, updatedFrame);
        }

        if (response) {
          // Fetch updated frames to ensure we have the correct image data
          const updatedFrames = await storyboardService.getProjectStoryboards(projectId);
          if (updatedFrames) {
            setFrames(updatedFrames);
          }
          
          setEditDialogOpen(false);
          setCurrentFrame(null);
          setError({ visible: true, message: 'Frame saved successfully', type: 'success' });
        }
      } catch (apiError) {
        console.error('API error when saving frame:', apiError);
        const saveFn = (frame) => frame._id
          ? storyboardService.updateStoryboard(projectId, frame)
          : storyboardService.createStoryboard(projectId, frame);
        const result = await retrySaveWithShotNumber(updatedFrame, sceneNumber, shotNumber, apiError, saveFn, askUser, frames);
        if (result.success) {
          // Fetch updated frames to ensure we have the correct image data
          const updatedFrames = await storyboardService.getProjectStoryboards(projectId);
          if (updatedFrames) setFrames(updatedFrames);
          setEditDialogOpen(false);
          setCurrentFrame(null);
          setError({ visible: true, message: `Frame saved${result.shotNumber ? ` with shot ${result.shotNumber}` : ' successfully'}`, type: 'success' });
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Error saving frame:', error);
      setError({
        visible: true,
        message: error.message || 'Failed to save frame. Please ensure all required fields are filled correctly.', 
        type: 'error' 
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Persistent Saved indicator + Error banner */}
        <Box sx={{ minHeight: 24, mb: 1.5, display: 'flex', alignItems: 'center' }}>
          {error.visible && error.type === 'error' ? (
            <Box sx={{
              py: 1.5,
              px: 2,
              borderRadius: 1,
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              border: '1px solid',
              borderColor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <ErrorOutlineIcon color="error" fontSize="small" />
              <Typography sx={{ fontSize: '0.875rem', color: 'error.main', fontWeight: 500 }}>
                {error.message}
              </Typography>
            </Box>
          ) : lastSaved ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'success.main',
              opacity: 0.8
            }}>
              <CheckCircleIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.02em' }}>
                Saved
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.5 }}>
                {lastSaved}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 20 }} />
          )}
        </Box>

        {/* Header Section - Minimal */}
        <Box sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          py: 1,
          px: 2,
          mb: 2
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                background: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <MovieIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: (theme) => theme.palette.text.primary
            }}>
              Storyboard
            </Typography>
            <Tooltip title="Aspect ratio" arrow>
              <IconButton
                onClick={handleOpenAspectRatioMenu}
                disabled={isUpdatingAspectRatio || isDragging}
                size="small"
                sx={{ color: theme.palette.primary.main }}
              >
                <AspectRatioIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={aspectRatioMenuAnchorEl}
              open={Boolean(aspectRatioMenuAnchorEl)}
              onClose={handleCloseAspectRatioMenu}
            >
              {aspectRatios.map((ratio) => (
                <MenuItem
                  key={ratio.value}
                  selected={ratio.value === defaultAspectRatio}
                  onClick={() => handleSelectAspectRatio(ratio.value)}
                >
                  {ratio.label}
                </MenuItem>
              ))}
            </Menu>
            {isUpdatingAspectRatio && (
              <CircularProgress size={16} />
            )}
            <Tooltip title="Add frame" arrow>
              <IconButton
                onClick={handleAddFrame}
                size="small"
                sx={{ color: theme.palette.primary.main }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
              {FEATURE_FLAGS.storyboardAssets && (
                <Tooltip title="Asset Library" arrow>
                  <IconButton
                    onClick={() => setAssetLibraryOpen(true)}
                    size="small"
                    sx={(theme) => ({
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent'
                    })}
                  >
                    <LibraryBooksIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {FEATURE_FLAGS.storyboardAI && (
                <Tooltip title="AI Generate" arrow>
                  <IconButton
                    onClick={() => {
                      setCurrentFrame(null);
                      setShouldAutoOpenQwen(true);
                      setEditDialogOpen(true);
                    }}
                    size="small"
                    sx={(theme) => ({
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent'
                    })}
                  >
                    <MotionPhotosAutoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
        </Box>

        {/* Asset Library dialog at storyboard level */}
        <Dialog
          open={assetLibraryOpen}
          onClose={() => setAssetLibraryOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LibraryBooksIcon fontSize="small" /> Asset Library
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
            {/* Create new asset */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2">Generate New Asset (z-image)</Typography>
                {assetLibraryError && (
                  <Alert severity="error">{assetLibraryError}</Alert>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={assetLibType}
                    label="Type"
                    onChange={(e) => setAssetLibType(e.target.value)}
                  >
                    <MenuItem value="character">Character</MenuItem>
                    <MenuItem value="location">Location</MenuItem>
                    <MenuItem value="vehicle">Vehicle</MenuItem>
                    <MenuItem value="prop">Prop</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Name"
                  value={assetLibName}
                  onChange={(e) => setAssetLibName(e.target.value)}
                  placeholder="Optional name"
                />
                <TextField
                  size="small"
                  label="Prompt"
                  multiline
                  minRows={3}
                  value={assetLibPrompt}
                  onChange={(e) => setAssetLibPrompt(e.target.value)}
                  placeholder="Describe the asset (style, pose, lighting)"
                />
                <TextField
                  size="small"
                  label="Seed"
                  value={assetLibSeed}
                  onChange={(e) => setAssetLibSeed(e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button onClick={() => setAssetLibraryOpen(false)}>Close</Button>
                  <Button
                    variant="contained"
                    onClick={handleGenerateAssetLibrary}
                    disabled={assetLibGenerating}
                  >
                    {assetLibGenerating ? <CircularProgress size={18} /> : 'Generate'}
                  </Button>
                </Box>
              </Box>

              {/* Library list */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Assets</Typography>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select
                      value={assetLibFilter}
                      label="Filter"
                      onChange={(e) => setAssetLibFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="character">Character</MenuItem>
                      <MenuItem value="location">Location</MenuItem>
                      <MenuItem value="vehicle">Vehicle</MenuItem>
                      <MenuItem value="prop">Prop</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {assetLibraryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {(assetLibrary || [])
                      .filter(a => assetLibFilter === 'all' || a.type === assetLibFilter)
                      .map(asset => (
                        <Grid item xs={12} sm={6} md={4} key={asset._id}>
                          <Card
                            onClick={() => {
                              setAssetLibName(asset.name || '');
                              setAssetPreview({ open: true, asset });
                            }}
                            sx={{
                              cursor: 'pointer',
                              height: '100%',
                              position: 'relative'
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, bgcolor: 'background.paper' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAssetLibrary(asset._id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <CardActionArea>
                              <Avatar
                                variant="rounded"
                                src={asset.imageUrl}
                                alt={asset.name}
                                sx={{ width: '100%', height: 96, borderRadius: 0 }}
                              />
                              <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="caption" noWrap>{asset.name || 'Untitled'}</Typography>
                                <Typography variant="caption" color="text.secondary">{asset.type}</Typography>
                              </Box>
                            </CardActionArea>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1 }}>
                              <Button size="small" onClick={() => handleSelectAssetForFrame(asset)}>
                                Use in frame
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                )}
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Asset preview dialog */}
        <Dialog
          open={assetPreview.open}
          onClose={() => setAssetPreview({ open: false, asset: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {assetPreview.asset?.name || 'Asset Preview'}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            {assetPreview.asset?.imageUrl ? (
              <Box sx={{ maxWidth: '100%', maxHeight: '70vh' }}>
                <img
                  src={assetPreview.asset.imageUrl}
                  alt={assetPreview.asset.name || 'Asset'}
                  style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: 4 }}
                />
              </Box>
            ) : (
              <Typography color="text.secondary">No image available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssetPreview({ open: false, asset: null })}>Close</Button>
            {assetPreview.asset && (
              <Button
                variant="contained"
                onClick={() => {
                  handleSelectAssetForFrame(assetPreview.asset);
                  setAssetPreview({ open: false, asset: null });
                }}
              >
                Use in frame
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Empty State - Minimal */}
        {frames.length === 0 && !loading ? (
          <Box sx={{
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 12, 
            px: 3,
            textAlign: 'center'
          }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: 1,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <MovieFilterIcon sx={{ 
                fontSize: 32, 
                color: 'primary.main'
              }} />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              mb: 1,
              color: 'text.primary'
            }}>
              No frames yet
            </Typography>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              maxWidth: 320,
              color: 'text.secondary'
            }}>
              Create your first storyboard frame to start visualizing your project.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAddFrame()}
              sx={{ 
                px: 3,
                py: 1,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Add Frame
            </Button>
          </Box>
        ) : (
          <BoardDropContainer
            frames={frames}
            dragState={dragState}
            lastMoveOp={lastMoveOp}
            serverUpdate={serverUpdate}
            sx={{
            width: '100%',
            height: frames.length > 20 ? '80vh' : 'auto', // Use fixed height for virtualization when many frames
            pb: 4
          }}
          >
            {frames.length > 20 ? (
              <div style={{ height: '100%', width: '100%' }}>
                {/* Use standard grid for large number of frames */}
                <Box sx={{
                  display: 'grid', 
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(220px, 1fr))',
                    md: 'repeat(auto-fill, minmax(240px, 1fr))',
                  },
                  gap: { xs: 0.5, sm: 1, md: 1.5 },
                  maxHeight: '80vh',
                  overflowY: 'auto'
                }}>
                  {frames.map((frame, index) => renderFrame({ frame, index }))}
                </Box>
              </div>
            ) : (
              // Use standard grid for small number of frames
              <Box sx={{
                display: 'grid', 
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(220px, 1fr))',
                  md: 'repeat(auto-fill, minmax(240px, 1fr))',
                },
                gap: { xs: 0.5, sm: 1, md: 1.5 },
              }}>
                {frames.map((frame, index) => renderFrame({ frame, index }))}
              </Box>
            )}
          </BoardDropContainer>
        )}

        <EditFrameDialog
          open={editDialogOpen}
          frame={currentFrame}
          onClose={() => {
            setEditDialogOpen(false);
            setCurrentFrame(null);
          }}
          onSave={handleSaveFrame}
          projectId={projectId}
          pendingAsset={pendingAssetForFrame}
          onConsumePendingAsset={() => setPendingAssetForFrame(null)}
          autoOpenQwen={shouldAutoOpenQwen}
          onConsumeAutoOpenQwen={() => setShouldAutoOpenQwen(false)}
          variantOnly={variantOnly}
          onSetVariantOnly={setVariantOnly}
        />

        <ConfirmDialog
          open={confirmDialog.open}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onClose}
          confirmLabel="Use suggested"
        />
      </Box>
    </DndProvider>
  );
};

export default Storyboard;
